import {
  useEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
  type RefObject,
} from 'react';

import { specialDocumentRequirementsEnabled } from '@autocontrol/api-contracts';

import type {
  AdminApi,
  ChecklistTemplateAdmin,
  DocumentTypeAdmin,
  PublishedTemplateVersionResult,
  TemplateAuthoringDraft,
  TemplateAuthoringPreview,
  TemplateAuthoringPreviewView,
  TemplateAuthoringValidationIssue,
  TemplateAuthoringValidationReport,
  TemplateFieldReuseCandidate,
} from './admin-api';
import { ApiError } from './api';
import { problemMessage, problemTechnicalDetail } from './admin-presentation';
import { templateBusinessLabel } from './template-admin-presentation';
import {
  AUTHORING_BINDING_KINDS,
  AUTHORING_CAPTURE_MODES,
  AUTHORING_FIELD_VALUE_TYPES,
  AUTHORING_OWNER_CODES,
  AUTHORING_PLACEMENTS,
  AUTHORING_PREVIEW_VIEWS,
  APPLICABILITY_RULE_TYPES,
  ITEM_VALIDATION_REQUIRED_WHEN,
  ITEM_VALIDATION_VALUE_TYPES,
  REQUIRED_RULE_TYPES,
  SNAPSHOT_PATH_FAMILIES,
  STATIC_SNAPSHOT_PATHS,
  authoringMasterAttributeLabel,
  authoringMasterObjectRoleLabel,
  masterAttributesForObjectRole,
  applyBindingBlueprintToField,
  authoringFieldFromReuseCandidate,
  authoringPayloadFromUnknown,
  bindImportProposalToDocumentType,
  blueprintFromField,
  confidenceLabel,
  createUnlinkedCaseDynamicField,
  emptyAuthoringField,
  emptyDocumentMetadataField,
  emptyDocumentRequirement,
  emptyItem,
  emptySection,
  excludeImportProposal,
  fieldReuseIdentityCriticalChanged,
  fieldReuseLinkBlockReason,
  findEquivalentField,
  forceUnlinkCaseDynamicSemanticKey,
  mergeAliasActionsIntoOverlay,
  metadataFieldsFromDocumentTypeDefaultSchema,
  moveItem,
  normalizeHeaderLabel,
  clearPendingAliasActionsForKey,
  rekeyPendingAliasActionsForGroup,
  reindexOrdinals,
  resolutionLabel,
  resolveSnapshotPath,
  serializeAuthoringPayload,
  withAuthoringBindingKind,
  type AuthoringApplicabilityRule,
  type AuthoringDocumentRequirementForm,
  type AuthoringFieldForm,
  type AuthoringItemForm,
  type AuthoringPayloadForm,
  type AuthoringRequiredRule,
  type AuthoringSectionForm,
  type HeaderAliasAction,
  type HeaderFieldCandidateForm,
  type ImportProposedDocumentForm,
} from './template-authoring-model';
import {
  AUTHORING_CONTRACTOR_APPLICABILITY_HINT,
  AUTHORING_PDF_METADATA_LABEL_HINT,
  AUTHORING_PLACEMENT_HINT,
  FIELD_REUSE_ADD_HINT,
  FIELD_REUSE_CREATE_NEW_CAPTION,
  FIELD_REUSE_IDENTITY_EDIT_HINT,
  FIELD_REUSE_LINKED_HINT,
  authoringApplicabilityRuleLabel,
  authoringBindingKindHint,
  authoringBindingKindLabel,
  authoringCaptureModeLabel,
  authoringDraftStatusLabel,
  authoringOwnerLabel,
  authoringPlacementLabel,
  authoringPreviewViewLabel,
  authoringProvenanceLabel,
  authoringRequiredRuleLabel,
  authoringSnapshotFamilyLabel,
  authoringSubjectBindingLabel,
  authoringValueTypeHint,
  authoringValueTypeLabel,
  canDiscardAuthoringDraft,
  canEditAuthoringDraft,
  documentTypeBusinessLabel,
  editorAreaForSubjectKind,
  fieldReuseDisplayTemplateLabel,
  fieldReuseLinkedStatusLine,
  fieldReuseUsedInSummary,
  isUnresolvedHeaderFieldCandidateIssue,
  unresolvedHeaderCandidateDisplay,
  validationIssueArea,
} from './template-authoring-presentation';
import { proposeNextVersionCode } from './template-authoring-version-code';
import {
  validationAfterValueTypeChange,
  isEnumValidationIncomplete,
  isBlankValidation,
} from './cfg-val-001';
import { CfgValValidationEditor } from './cfg-val-001-editor';
import { generateInternalCode } from './admin-internal-codes';
import { emptyMetadataSchema } from './configuration-catalog-presentation';

type EditorArea =
  'general' | 'fields' | 'documents' | 'sections' | 'presentation' | 'validation' | 'publish';

async function persistPendingAliasLearning(input: {
  readonly api: AdminApi;
  readonly savedDraft: TemplateAuthoringDraft;
  readonly pendingAliases: readonly HeaderAliasAction[];
}): Promise<{ readonly changed: boolean }> {
  if (input.pendingAliases.length < 1 || !input.savedDraft.import_run_id) {
    return { changed: false };
  }
  const savedForm = authoringPayloadFromUnknown(input.savedDraft.authoring_payload);
  const run = await input.api.getImportRun(input.savedDraft.import_run_id);
  const profiles = await input.api.listImportProfiles();
  let profileId: string | null = null;
  let expectedVersion: string | null = null;
  let mapping: Record<string, unknown> | null = null;
  let existingOverlay: Record<string, unknown> = {
    schema: 'header-binding-overlay/v1',
    aliases: [],
  };
  for (const profile of profiles) {
    const revisions = await input.api.listImportRevisions(profile.id);
    if (
      revisions.items.some((row) => row.id === run.import_profile_revision_id) ||
      profile.current_revision_id === run.import_profile_revision_id
    ) {
      profileId = profile.id;
      expectedVersion = profile.version;
      const currentId = profile.current_revision_id ?? run.import_profile_revision_id;
      const revision = await input.api.getImportRevision(profile.id, currentId);
      mapping = revision.mapping;
      if (
        revision.header_binding_overlay !== null &&
        revision.header_binding_overlay !== undefined &&
        typeof revision.header_binding_overlay === 'object'
      ) {
        existingOverlay = revision.header_binding_overlay;
      }
      break;
    }
  }
  if (profileId === null || expectedVersion === null || mapping === null) {
    throw new Error('Import profile for alias learning was not found');
  }
  const rebuiltActions: HeaderAliasAction[] = input.pendingAliases.map((action) => {
    const labelKey = normalizeHeaderLabel(action.normalized_label);
    const groupKey =
      action.normalized_group === null ? null : normalizeHeaderLabel(action.normalized_group);
    const candidate = savedForm.header_binding_review?.candidates.find((row) => {
      const rowLabel = normalizeHeaderLabel(row.normalized_label);
      const rowGroup = row.group_title === null ? null : normalizeHeaderLabel(row.group_title);
      return rowLabel === labelKey && rowGroup === groupKey;
    });
    const fieldKey = candidate?.resolution.resulting_field_key;
    const field =
      fieldKey === undefined
        ? undefined
        : savedForm.fields.find((row) => row.field_key === fieldKey);
    if (field === undefined) {
      return action;
    }
    return {
      ...action,
      normalized_label: labelKey,
      normalized_group: groupKey,
      binding_blueprint: blueprintFromField(field),
    };
  });
  const { overlay, changed } = mergeAliasActionsIntoOverlay(existingOverlay, rebuiltActions);
  if (changed) {
    await input.api.createImportRevision(profileId, expectedVersion, mapping, overlay);
  }
  return { changed };
}

const EDITOR_DOCUMENT_AREA: { id: EditorArea; label: string } = {
  id: 'documents',
  label: 'Документы',
};

const EDITOR_AREAS: readonly { id: EditorArea; label: string }[] = [
  { id: 'general', label: 'Общие сведения' },
  { id: 'fields', label: 'Поля предзаполнения' },
  ...(specialDocumentRequirementsEnabled() ? [EDITOR_DOCUMENT_AREA] : []),
  { id: 'sections', label: 'Разделы и пункты' },
  { id: 'presentation', label: 'Отображение / PDF' },
  { id: 'validation', label: 'Проверка и Preview' },
  { id: 'publish', label: 'Публикация' },
];

function ProblemAlert(props: { readonly failure: unknown }): ReactElement {
  const technical = problemTechnicalDetail(props.failure);
  return (
    <div role="alert" className="banner banner-closed">
      <p>{problemMessage(props.failure)}</p>
      {technical !== null ? <p className="meta">Технические сведения: {technical}</p> : null}
    </div>
  );
}

function TechnicalDetails(props: { readonly children: ReactNode }): ReactElement {
  return (
    <details className="technical">
      <summary>Технические сведения</summary>
      {props.children}
    </details>
  );
}

export function DraftEditor(props: {
  readonly api: AdminApi;
  readonly draftId: string;
  readonly templates: ChecklistTemplateAdmin[] | null;
  readonly onBack: () => void;
  readonly onPublished?: (result: PublishedTemplateVersionResult) => void;
}): ReactElement {
  const [serverDraft, setServerDraft] = useState<TemplateAuthoringDraft | null>(null);
  const [form, setForm] = useState<AuthoringPayloadForm | null>(null);
  const [dirty, setDirty] = useState(false);
  const [area, setArea] = useState<EditorArea>('general');
  const [documentTypes, setDocumentTypes] = useState<DocumentTypeAdmin[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [validation, setValidation] = useState<TemplateAuthoringValidationReport | null>(null);
  const [preview, setPreview] = useState<TemplateAuthoringPreview | null>(null);
  const [previewView, setPreviewView] = useState<TemplateAuthoringPreviewView>('CONTRACTOR');
  const [versionCode, setVersionCode] = useState('');
  const [published] = useState<PublishedTemplateVersionResult | null>(null);
  const [focusUnresolvedDocs, setFocusUnresolvedDocs] = useState(false);
  const [focusHeaderCandidateId, setFocusHeaderCandidateId] = useState<string | null>(null);
  const [focusHeaderCandidateNotice, setFocusHeaderCandidateNotice] = useState<string | null>(null);
  /** Prefill «Добавить поле» composer — lifted so global Save cannot silently drop it. */
  const [fieldComposerOpen, setFieldComposerOpen] = useState(false);
  const loadSeq = useRef(0);
  const versionSuggestSeq = useRef(0);
  const unresolvedDocsRef = useRef<HTMLElement | null>(null);

  const template =
    props.templates?.find((row) => row.id === serverDraft?.checklist_template_id) ?? null;
  const editable = serverDraft !== null && canEditAuthoringDraft(serverDraft.status);

  const applyServerDraft = (draft: TemplateAuthoringDraft) => {
    setServerDraft(draft);
    setForm(authoringPayloadFromUnknown(draft.authoring_payload));
    setDirty(false);
    setValidation(draft.validation_report);
    setConflict(false);
    setFieldComposerOpen(false);
  };

  const reload = () => {
    const seq = ++loadSeq.current;
    setBusy(true);
    setError(null);
    void Promise.all([
      props.api.getTemplateAuthoringDraft(props.draftId),
      props.api.listDocumentTypes({ status: 'ACTIVE' }),
    ])
      .then(([draft, docs]) => {
        if (seq !== loadSeq.current) {
          return;
        }
        applyServerDraft(draft);
        setDocumentTypes(docs);
        setBusy(false);
      })
      .catch((failure: unknown) => {
        if (seq !== loadSeq.current) {
          return;
        }
        setError(failure);
        setBusy(false);
      });
  };

  useEffect(() => {
    reload();
  }, [props.api, props.draftId]);

  useEffect(() => {
    if (serverDraft === null) {
      return;
    }
    const seq = ++versionSuggestSeq.current;
    void props.api
      .listChecklistTemplateVersions(serverDraft.checklist_template_id)
      .then((versions) => {
        if (seq !== versionSuggestSeq.current) {
          return;
        }
        setVersionCode((current) =>
          current.trim().length > 0
            ? current
            : proposeNextVersionCode(versions.map((row) => row.version_code)),
        );
      })
      .catch(() => {
        /* suggestion is best-effort; publish still validates uniqueness server-side */
      });
  }, [props.api, serverDraft?.checklist_template_id, serverDraft?.id]);

  useEffect(() => {
    if (!focusUnresolvedDocs || area !== 'documents') {
      return;
    }
    const node = unresolvedDocsRef.current;
    if (node !== null && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    node?.focus?.();
    setFocusUnresolvedDocs(false);
  }, [focusUnresolvedDocs, area, form?.import_evidence?.proposed_documents.length]);

  const updateForm = (updater: (current: AuthoringPayloadForm) => AuthoringPayloadForm) => {
    setForm((current) => {
      if (current === null) {
        return current;
      }
      return updater(current);
    });
    setDirty(true);
    setSuccess(null);
  };

  const mutate = (work: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    setConflict(false);
    void work()
      .catch((failure: unknown) => {
        if (failure instanceof ApiError && failure.problem.code === 'STALE_VERSION') {
          setConflict(true);
        }
        setError(failure);
      })
      .finally(() => setBusy(false));
  };

  const exitToList = () => {
    if (dirty) {
      const leave = window.confirm(
        'Выйти без сохранения? Несохранённые изменения в форме будут потеряны.',
      );
      if (!leave) {
        return;
      }
    }
    props.onBack();
  };

  if (serverDraft === null || form === null) {
    return (
      <>
        <button type="button" onClick={props.onBack}>
          К списку
        </button>
        {error !== null ? <ProblemAlert failure={error} /> : null}
        <p role="status">Загрузка черновика…</p>
      </>
    );
  }

  return (
    <>
      <button type="button" onClick={exitToList}>
        К списку
      </button>
      <h2>Редактор черновика</h2>
      <p>
        {template !== null ? templateBusinessLabel(template) : 'Чек-лист'}
        {' · '}
        {authoringProvenanceLabel(serverDraft.provenance)}
        {' · '}
        {authoringDraftStatusLabel(serverDraft.status)}
      </p>
      <TechnicalDetails>
        <p>Идентификатор черновика: {serverDraft.id}</p>
        <p>Код чек-листа: {template?.template_code ?? serverDraft.checklist_template_id}</p>
        <p>Техническая ревизия черновика (оптимистичная блокировка): {serverDraft.version}</p>
      </TechnicalDetails>
      <p className="meta" role="status" data-testid="authoring-dirty-indicator">
        {dirty
          ? 'Есть несохранённые локальные изменения.'
          : 'Локальная форма совпадает с последним сохранённым серверным черновиком.'}
        {serverDraft.cloned_from_template_version_id !== null &&
        serverDraft.cloned_from_template_version_id !== undefined
          ? ' Исходная опубликованная версия неизменяема; правки относятся только к этому черновику.'
          : ''}
      </p>
      {error !== null ? <ProblemAlert failure={error} /> : null}
      {conflict ? (
        <div role="alert" className="banner banner-conflict">
          <p>
            Конфликт версии (OCC). Сохранение не выполнено. Локальные правки сохранены в форме.
            Перезагрузите серверное состояние, затем повторите сохранение.
          </p>
          <button type="button" disabled={busy} onClick={reload}>
            Перезагрузить с сервера
          </button>
        </div>
      ) : null}
      {success !== null ? (
        <p role="status" className="banner">
          {success}
        </p>
      ) : null}
      <div className="section-tabs" role="tablist" aria-label="Области конструктора">
        {EDITOR_AREAS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={area === item.id}
            className={area === item.id ? 'nav-current' : undefined}
            onClick={() => {
              if (item.id !== 'fields' && fieldComposerOpen) {
                setFieldComposerOpen(false);
              }
              setArea(item.id);
            }}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div className="actions authoring-editor-toolbar">
        <button
          type="button"
          className="btn-primary"
          disabled={
            busy ||
            !editable ||
            !dirty ||
            form.fields.some((field) =>
              isEnumValidationIncomplete(field.value_type, field.validation),
            ) ||
            form.document_requirements.some((doc) =>
              doc.metadata_fields.some((meta) =>
                isEnumValidationIncomplete(meta.value_type, meta.validation),
              ),
            )
          }
          onClick={() => {
            if (fieldComposerOpen) {
              setArea('fields');
              setError(
                new ApiError(400, {
                  code: 'VALIDATION_ERROR',
                  detail:
                    'Завершите или отмените создание поля в форме «Добавить поле», затем сохраните черновик. Незавершённое поле не сохраняется.',
                }),
              );
              return;
            }
            if (
              form.fields.some((field) =>
                isEnumValidationIncomplete(field.value_type, field.validation),
              ) ||
              form.document_requirements.some((doc) =>
                doc.metadata_fields.some((meta) =>
                  isEnumValidationIncomplete(meta.value_type, meta.validation),
                ),
              )
            ) {
              setError(
                new Error(
                  'Добавьте хотя бы одно непустое разрешённое значение для полей типа ENUM',
                ),
              );
              return;
            }
            mutate(async () => {
              const pendingAliases = [...form.pending_alias_actions];
              const saved = await props.api.updateTemplateAuthoringDraft(
                serverDraft.id,
                serverDraft.version,
                serializeAuthoringPayload(form),
              );
              applyServerDraft(saved);
              if (pendingAliases.length > 0 && saved.import_run_id) {
                try {
                  const { changed } = await persistPendingAliasLearning({
                    api: props.api,
                    savedDraft: saved,
                    pendingAliases,
                  });
                  setForm((current) =>
                    current === null
                      ? current
                      : { ...current, pending_alias_actions: [], alias_learning_error: null },
                  );
                  setSuccess(
                    changed
                      ? 'Черновик сохранён. Обучение алиасов записано в профиль.'
                      : 'Черновик сохранён.',
                  );
                } catch (learningError) {
                  const message =
                    learningError instanceof Error
                      ? learningError.message
                      : 'не удалось сохранить обучение алиасов';
                  setForm((current) =>
                    current === null
                      ? current
                      : {
                          ...current,
                          pending_alias_actions: pendingAliases,
                          alias_learning_error: message,
                        },
                  );
                  setSuccess(
                    'Черновик сохранён, но обучение алиасов не сохранено. Алиас не отмечен как trusted/saved.',
                  );
                }
              } else {
                setSuccess('Черновик сохранён.');
              }
            });
          }}
        >
          Сохранить
        </button>
        <button type="button" disabled={busy} onClick={exitToList}>
          Отмена
        </button>
        {canDiscardAuthoringDraft(serverDraft.status) ? (
          <button
            type="button"
            className="authoring-editor-toolbar-delete"
            disabled={busy}
            onClick={() => {
              if (!window.confirm('Исключить черновик из редактирования?')) {
                return;
              }
              mutate(async () => {
                const discarded = await props.api.discardTemplateAuthoringDraft(
                  serverDraft.id,
                  serverDraft.version,
                );
                applyServerDraft(discarded);
                setSuccess('Черновик исключён из редактирования.');
              });
            }}
          >
            Удалить
          </button>
        ) : null}
      </div>

      {area === 'general' ? (
        <GeneralArea form={form} disabled={busy || !editable} onChange={updateForm} />
      ) : null}
      {area === 'fields' ? (
        <FieldsArea
          api={props.api}
          checklistTemplateId={serverDraft?.checklist_template_id ?? null}
          form={form}
          disabled={busy || !editable}
          composerOpen={fieldComposerOpen}
          onComposerOpenChange={setFieldComposerOpen}
          focusCandidateId={focusHeaderCandidateId}
          onFocusCandidateHandled={(result) => {
            setFocusHeaderCandidateId(null);
            if (result === 'fallback') {
              setFocusHeaderCandidateNotice(
                'Точный кандидат не найден в списке. Показан блок «Найденные поля шапки».',
              );
            } else {
              setFocusHeaderCandidateNotice(null);
            }
          }}
          onChange={updateForm}
          onRetryAliasLearning={() => {
            if (serverDraft === null || form === null) return;
            const pendingAliases = [...form.pending_alias_actions];
            if (pendingAliases.length < 1 || !serverDraft.import_run_id) return;
            mutate(async () => {
              try {
                const { changed } = await persistPendingAliasLearning({
                  api: props.api,
                  savedDraft: serverDraft,
                  pendingAliases,
                });
                setForm((current) =>
                  current === null
                    ? current
                    : { ...current, pending_alias_actions: [], alias_learning_error: null },
                );
                setSuccess(
                  changed
                    ? 'Обучение алиасов записано в профиль.'
                    : 'Повторное сохранение привязок не потребовало новой версии профиля.',
                );
              } catch (learningError) {
                const message =
                  learningError instanceof Error
                    ? learningError.message
                    : 'не удалось сохранить обучение алиасов';
                setForm((current) =>
                  current === null
                    ? current
                    : {
                        ...current,
                        pending_alias_actions: pendingAliases,
                        alias_learning_error: message,
                      },
                );
                setSuccess(null);
              }
            });
          }}
        />
      ) : null}
      {area === 'documents' ? (
        <DocumentsArea
          form={form}
          documentTypes={documentTypes}
          disabled={busy || !editable}
          unresolvedSectionRef={unresolvedDocsRef}
          api={props.api}
          onChange={updateForm}
          onDocumentTypesChanged={setDocumentTypes}
          onBusyError={(failure) => setError(failure)}
          onBusySuccess={(message) => setSuccess(message)}
          setBusy={setBusy}
        />
      ) : null}
      {area === 'sections' ? (
        <SectionsArea form={form} disabled={busy || !editable} onChange={updateForm} />
      ) : null}
      {area === 'presentation' ? (
        <PresentationArea form={form} disabled={busy || !editable} onChange={updateForm} />
      ) : null}
      {area === 'validation' ? (
        <ValidationPreviewArea
          dirty={dirty}
          draftId={serverDraft.id}
          draftStatus={serverDraft.status}
          draftVersion={serverDraft.version}
          validation={validation}
          form={form}
          preview={preview}
          previewView={previewView}
          busy={busy}
          onPreviewViewChange={setPreviewView}
          onValidate={() => {
            mutate(async () => {
              if (dirty) {
                throw new ApiError(400, {
                  code: 'VALIDATION_ERROR',
                  detail:
                    'Сначала сохраните черновик. Серверная проверка работает по последнему сохранённому состоянию.',
                });
              }
              const report = await props.api.validateTemplateAuthoringDraft(
                serverDraft.id,
                serverDraft.version,
              );
              const refreshed = await props.api.getTemplateAuthoringDraft(serverDraft.id);
              applyServerDraft({
                ...refreshed,
                validation_report: report,
              });
              setValidation(report);
              setSuccess(
                report.errors.length === 0
                  ? 'Серверная проверка пройдена без ошибок.'
                  : `Серверная проверка: ошибок ${report.errors.length}.`,
              );
            });
          }}
          onPreview={() => {
            mutate(async () => {
              if (dirty) {
                throw new ApiError(400, {
                  code: 'VALIDATION_ERROR',
                  detail:
                    'Превью строится по последнему сохранённому серверному состоянию. Сначала сохраните изменения.',
                });
              }
              const result = await props.api.previewTemplateAuthoringDraft(
                serverDraft.id,
                previewView,
              );
              setPreview(result);
              setSuccess(`Превью ${authoringPreviewViewLabel(previewView)} загружено с сервера.`);
            });
          }}
          onJump={(issue) => {
            const nextArea = editorAreaForSubjectKind(issue.subject_kind) as EditorArea;
            setArea(nextArea);
            if (issue.subject_kind === 'DOCUMENT') {
              setFocusUnresolvedDocs(true);
            }
            if (isUnresolvedHeaderFieldCandidateIssue(issue)) {
              setFocusHeaderCandidateId(issue.subject_id);
              setFocusHeaderCandidateNotice(null);
            }
          }}
        />
      ) : null}
      {focusHeaderCandidateNotice !== null && area === 'fields' ? (
        <p role="status" className="banner">
          {focusHeaderCandidateNotice}
        </p>
      ) : null}
      {area === 'publish' ? (
        <PublishArea
          draft={serverDraft}
          validation={validation}
          dirty={dirty}
          versionCode={versionCode}
          published={published}
        />
      ) : null}

      <TechnicalDetails>
        <p>draft_id: {serverDraft.id}</p>
        <p>checklist_template_id: {serverDraft.checklist_template_id}</p>
        {serverDraft.import_run_id !== null && serverDraft.import_run_id !== undefined ? (
          <p>import_run_id: {serverDraft.import_run_id}</p>
        ) : null}
        {serverDraft.cloned_from_template_version_id !== null &&
        serverDraft.cloned_from_template_version_id !== undefined ? (
          <p>cloned_from_template_version_id: {serverDraft.cloned_from_template_version_id}</p>
        ) : null}
        {serverDraft.published_template_version_id !== null &&
        serverDraft.published_template_version_id !== undefined ? (
          <p>published_template_version_id: {serverDraft.published_template_version_id}</p>
        ) : null}
      </TechnicalDetails>
    </>
  );
}

function RequiredRuleEditor(props: {
  readonly id: string;
  readonly value: AuthoringRequiredRule;
  readonly disabled: boolean;
  readonly onChange: (value: AuthoringRequiredRule) => void;
  readonly label?: string;
}): ReactElement {
  return (
    <label htmlFor={props.id}>
      {props.label ?? 'Нужно ли заполнять?'}
      <select
        id={props.id}
        value={props.value.type}
        disabled={props.disabled}
        title="Отдельное правило обязательности. «Всегда обязательно» действует даже если поле сейчас не используется. «Обязательно, только когда используется» — только при применимости. «Необязательно» — можно не заполнять."
        onChange={(event) =>
          props.onChange({
            version: 1,
            type: event.target.value as AuthoringRequiredRule['type'],
          })
        }
      >
        {REQUIRED_RULE_TYPES.map((type) => (
          <option key={type} value={type}>
            {authoringRequiredRuleLabel(type)}
          </option>
        ))}
      </select>
    </label>
  );
}

function ApplicabilityRuleEditor(props: {
  readonly id: string;
  readonly value: AuthoringApplicabilityRule;
  readonly disabled: boolean;
  readonly onChange: (value: AuthoringApplicabilityRule) => void;
  readonly label?: string;
  readonly showContractorWarning?: boolean;
}): ReactElement {
  return (
    <>
      <label htmlFor={props.id}>
        {props.label ?? 'Когда используется?'}
        <select
          id={props.id}
          value={props.value.type}
          disabled={props.disabled}
          title="Отдельное правило: показывается ли поле. Не подменяет ответ на вопрос «Нужно ли заполнять?»."
          onChange={(event) => {
            const type = event.target.value as AuthoringApplicabilityRule['type'];
            if (type === 'FIELD_EQUALS') {
              props.onChange({
                version: 1,
                type,
                field_path: props.value.field_path ?? '',
                value: props.value.value ?? '',
              });
              return;
            }
            if (type === 'ONE_OF') {
              props.onChange({
                version: 1,
                type,
                field_path: props.value.field_path ?? '',
                values_text: props.value.values_text ?? '',
              });
              return;
            }
            props.onChange({ version: 1, type });
          }}
        >
          {APPLICABILITY_RULE_TYPES.map((type) => (
            <option key={type} value={type}>
              {authoringApplicabilityRuleLabel(type)}
            </option>
          ))}
        </select>
      </label>
      {props.showContractorWarning === true && props.value.type !== 'ALWAYS' ? (
        <p
          className="meta authoring-hint-warning"
          tabIndex={0}
          title={AUTHORING_CONTRACTOR_APPLICABILITY_HINT}
        >
          Для подрядчика условие «когда используется» может не сработать при предзаполнении.
        </p>
      ) : null}
      {props.value.type === 'FIELD_EQUALS' || props.value.type === 'ONE_OF' ? (
        <label htmlFor={`${props.id}_path`}>
          Путь поля
          <input
            id={`${props.id}_path`}
            value={props.value.field_path ?? ''}
            disabled={props.disabled}
            onChange={(event) => props.onChange({ ...props.value, field_path: event.target.value })}
          />
        </label>
      ) : null}
      {props.value.type === 'FIELD_EQUALS' ? (
        <label htmlFor={`${props.id}_value`}>
          Значение
          <input
            id={`${props.id}_value`}
            value={props.value.value ?? ''}
            disabled={props.disabled}
            onChange={(event) => props.onChange({ ...props.value, value: event.target.value })}
          />
        </label>
      ) : null}
      {props.value.type === 'ONE_OF' ? (
        <label htmlFor={`${props.id}_values`}>
          Значения через запятую
          <input
            id={`${props.id}_values`}
            value={props.value.values_text ?? ''}
            disabled={props.disabled}
            onChange={(event) =>
              props.onChange({ ...props.value, values_text: event.target.value })
            }
          />
        </label>
      ) : null}
    </>
  );
}

function GeneralArea(props: {
  readonly form: AuthoringPayloadForm;
  readonly disabled: boolean;
  readonly onChange: (updater: (current: AuthoringPayloadForm) => AuthoringPayloadForm) => void;
}): ReactElement {
  return (
    <fieldset>
      <legend>Общий контекст</legend>
      <label htmlFor="tad_general_name">
        Название
        <input
          id="tad_general_name"
          value={props.form.general_name}
          disabled={props.disabled}
          onChange={(event) =>
            props.onChange((current) => ({ ...current, general_name: event.target.value }))
          }
        />
      </label>
      <label htmlFor="tad_general_description">
        Описание
        <input
          id="tad_general_description"
          value={props.form.general_description}
          disabled={props.disabled}
          onChange={(event) =>
            props.onChange((current) => ({
              ...current,
              general_description: event.target.value,
            }))
          }
        />
      </label>
    </fieldset>
  );
}

function FieldsArea(props: {
  readonly api: AdminApi;
  readonly checklistTemplateId: string | null;
  readonly form: AuthoringPayloadForm;
  readonly disabled: boolean;
  readonly composerOpen: boolean;
  readonly onComposerOpenChange: (open: boolean) => void;
  readonly focusCandidateId?: string | null;
  readonly onFocusCandidateHandled?: (result: 'found' | 'fallback') => void;
  readonly onChange: (updater: (current: AuthoringPayloadForm) => AuthoringPayloadForm) => void;
  readonly onRetryAliasLearning?: () => void;
}): ReactElement {
  const review = props.form.header_binding_review;
  const headerCandidatesRegionRef = useRef<HTMLElement | null>(null);
  const [chooseOther, setChooseOther] = useState<
    Record<string, { selecting: boolean; selectedKey: string }>
  >({});
  const [fieldUiMode, setFieldUiMode] = useState<'constructor' | 'advanced'>('constructor');
  const [expandedFieldId, setExpandedFieldId] = useState<string | null>(null);
  const [expandedCandidateId, setExpandedCandidateId] = useState<string | null>(null);
  const [highlightCandidateId, setHighlightCandidateId] = useState<string | null>(null);
  const [focusLabelFieldId, setFocusLabelFieldId] = useState<string | null>(null);
  const [addLabel, setAddLabel] = useState('');
  const [composerError, setComposerError] = useState<string | null>(null);
  const [searchState, setSearchState] = useState<'idle' | 'loading' | 'empty' | 'error' | 'results'>(
    'idle',
  );
  const [candidates, setCandidates] = useState<TemplateFieldReuseCandidate[]>([]);
  const [linkRowError, setLinkRowError] = useState<Record<string, string>>({});
  const [linkedByKey, setLinkedByKey] = useState<ReadonlyMap<string, TemplateFieldReuseCandidate>>(
    new Map(),
  );
  const [linkedLookupReady, setLinkedLookupReady] = useState(false);
  const [usagePopoverFieldId, setUsagePopoverFieldId] = useState<string | null>(null);
  const [identityEditHintFieldId, setIdentityEditHintFieldId] = useState<string | null>(null);
  const addLabelInputRef = useRef<HTMLInputElement | null>(null);
  const searchSeq = useRef(0);
  const linkedSeq = useRef(0);
  const unresolvedCount =
    review?.candidates.filter((candidate) => candidate.resolution.state === 'UNRESOLVED').length ??
    0;
  const resolvedCount =
    review?.candidates.filter((candidate) => candidate.resolution.state !== 'UNRESOLVED').length ??
    0;

  useEffect(() => {
    const targetId = props.focusCandidateId;
    if (targetId === null || targetId === undefined || targetId.trim().length < 1) {
      return;
    }
    const exists =
      review?.candidates.some((candidate) => candidate.candidate_id === targetId) === true;
    if (exists) {
      setExpandedCandidateId(targetId);
      setHighlightCandidateId(targetId);
      window.requestAnimationFrame(() => {
        const node = document.getElementById(`header-candidate-${targetId}`);
        if (node !== null && typeof node.scrollIntoView === 'function') {
          node.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
      props.onFocusCandidateHandled?.('found');
      const timer = window.setTimeout(() => {
        setHighlightCandidateId((current) => (current === targetId ? null : current));
      }, 2500);
      return () => window.clearTimeout(timer);
    }
    const region = headerCandidatesRegionRef.current;
    if (region !== null && typeof region.scrollIntoView === 'function') {
      region.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    props.onFocusCandidateHandled?.('fallback');
    return undefined;
  }, [props.focusCandidateId, review?.candidates]);

  useEffect(() => {
    if (!props.composerOpen) {
      return;
    }
    const input = addLabelInputRef.current;
    if (input !== null) {
      input.focus();
    }
  }, [props.composerOpen]);

  useEffect(() => {
    if (!props.composerOpen) {
      return;
    }
    const trimmed = addLabel.trim();
    if (trimmed.length < 2) {
      searchSeq.current += 1;
      setCandidates([]);
      setSearchState('idle');
      setLinkRowError({});
      return;
    }
    const seq = ++searchSeq.current;
    setSearchState('loading');
    const timer = window.setTimeout(() => {
      void props.api
        .listTemplateFieldReuseCandidates({
          q: trimmed.slice(0, 120),
          excludeChecklistTemplateId: props.checklistTemplateId ?? undefined,
        })
        .then((rows) => {
          if (seq !== searchSeq.current) {
            return;
          }
          setCandidates(rows);
          setSearchState(rows.length === 0 ? 'empty' : 'results');
          setLinkRowError({});
        })
        .catch(() => {
          if (seq !== searchSeq.current) {
            return;
          }
          setCandidates([]);
          setSearchState('error');
        });
    }, 250);
    return () => {
      window.clearTimeout(timer);
    };
  }, [addLabel, props.composerOpen, props.api, props.checklistTemplateId]);

  useEffect(() => {
    const keys = [
      ...new Set(
        props.form.fields
          .filter((row) => row.binding_kind === 'CASE_DYNAMIC' && row.semantic_key.trim().length > 0)
          .map((row) => row.semantic_key.trim()),
      ),
    ];
    if (keys.length === 0) {
      linkedSeq.current += 1;
      setLinkedByKey(new Map());
      setLinkedLookupReady(true);
      return;
    }
    const seq = ++linkedSeq.current;
    setLinkedLookupReady(false);
    const batches: string[][] = [];
    for (let index = 0; index < keys.length; index += 25) {
      batches.push(keys.slice(index, index + 25));
    }
    void (async () => {
      try {
        const maps: TemplateFieldReuseCandidate[] = [];
        for (const batch of batches) {
          const rows = await props.api.listTemplateFieldReuseCandidates({
            semanticKeys: batch,
            excludeChecklistTemplateId: props.checklistTemplateId ?? undefined,
          });
          if (seq !== linkedSeq.current) {
            return;
          }
          maps.push(...rows);
        }
        if (seq !== linkedSeq.current) {
          return;
        }
        const next = new Map<string, TemplateFieldReuseCandidate>();
        for (const row of maps) {
          if (row.binding_kind !== 'CASE_DYNAMIC') {
            continue;
          }
          const key = row.semantic_key.trim();
          if (key.length < 1 || row.used_in.length < 1) {
            continue;
          }
          next.set(key, row);
        }
        setLinkedByKey(next);
        setLinkedLookupReady(true);
      } catch {
        if (seq !== linkedSeq.current) {
          return;
        }
        setLinkedByKey(new Map());
        setLinkedLookupReady(true);
      }
    })();
  }, [props.api, props.checklistTemplateId, props.form.fields]);

  const closeAddPanel = () => {
    searchSeq.current += 1;
    props.onComposerOpenChange(false);
    setAddLabel('');
    setCandidates([]);
    setSearchState('idle');
    setLinkRowError({});
    setComposerError(null);
  };

  const appendFieldAndFocus = (field: AuthoringFieldForm) => {
    props.onChange((current) => ({
      ...current,
      fields: reindexOrdinals([...current.fields, field]),
    }));
    const nextIndex = props.form.fields.length;
    const fieldId = authoringFieldRowId(field, nextIndex);
    setExpandedFieldId(fieldId);
    setFocusLabelFieldId(fieldId);
    closeAddPanel();
  };

  const queueSuggestedAlias = (
    current: AuthoringPayloadForm,
    candidate: HeaderFieldCandidateForm,
    field: AuthoringFieldForm,
  ): HeaderAliasAction[] => {
    const normalizedLabel = normalizeHeaderLabel(candidate.normalized_label);
    const normalizedGroup =
      candidate.group_title === null ? null : normalizeHeaderLabel(candidate.group_title);
    return [
      ...current.pending_alias_actions.filter(
        (action) =>
          !(
            normalizeHeaderLabel(action.normalized_label) === normalizedLabel &&
            (action.normalized_group === null
              ? null
              : normalizeHeaderLabel(action.normalized_group)) === normalizedGroup
          ),
      ),
      {
        normalized_label: normalizedLabel,
        normalized_group: normalizedGroup,
        status: 'SUGGESTED',
        binding_blueprint: blueprintFromField(field),
        raw_label_examples: [candidate.raw_label],
      },
    ];
  };

  const boundFieldLabel = (candidate: HeaderFieldCandidateForm): string | null => {
    const key = candidate.resolution.resulting_field_key;
    if (key === undefined) return null;
    const field = props.form.fields.find((row) => row.field_key === key);
    return field?.label ?? null;
  };

  const renderCandidateCard = (candidate: HeaderFieldCandidateForm): ReactElement => {
    const chooseState = chooseOther[candidate.candidate_id];
    const businessLabel = boundFieldLabel(candidate);
    const expanded = expandedCandidateId === candidate.candidate_id;
    const unresolved = candidate.resolution.state === 'UNRESOLVED';
    const summaryLine = [
      resolutionLabel(candidate.resolution.state),
      confidenceLabel(candidate.confidence),
      businessLabel !== null ? `поле: ${businessLabel}` : null,
      candidate.suggested_binding !== null
        ? `предложение: ${authoringBindingKindLabel(
            String(candidate.suggested_binding.binding_kind ?? ''),
          )}`
        : null,
    ]
      .filter((part): part is string => part !== null)
      .join(' · ');
    const rowClass = [
      'authoring-field-row',
      expanded ? 'authoring-field-row-expanded' : '',
      highlightCandidateId === candidate.candidate_id ? 'authoring-field-row-focus' : '',
    ]
      .filter((part) => part.length > 0)
      .join(' ');
    return (
      <li
        key={candidate.candidate_id}
        id={`header-candidate-${candidate.candidate_id}`}
        className={rowClass}
      >
        <div className="authoring-field-row-main">
          <button
            type="button"
            className="authoring-field-summary"
            aria-expanded={expanded}
            title={candidate.raw_label}
            onClick={() =>
              setExpandedCandidateId((current) =>
                current === candidate.candidate_id ? null : candidate.candidate_id,
              )
            }
          >
            <span className="authoring-field-title">{candidate.raw_label}</span>
            <p className="authoring-field-summary-line meta">
              {unresolved ? (
                <span className="authoring-field-badge">требует решения</span>
              ) : null}{' '}
              {summaryLine}
            </p>
          </button>
          <div className="authoring-field-actions">
            <button
              type="button"
              className="authoring-field-icon-btn"
              title="Принять привязку"
              aria-label="Принять привязку"
              disabled={props.disabled || candidate.suggested_binding === null}
              onClick={() => {
                if (candidate.suggested_binding === null) return;
                props.onChange((current) => {
                  const blueprint = candidate.suggested_binding!;
                  const existing = findEquivalentField(
                    current.fields,
                    normalizeHeaderLabel(candidate.raw_label),
                    blueprint,
                  );
                  let fields = [...current.fields];
                  let fieldKey = existing?.field_key;
                  let boundField = existing;
                  if (existing === undefined) {
                    fieldKey = `hdr_${candidate.candidate_id.slice(-12)}`;
                    const created = {
                      ...applyBindingBlueprintToField(
                        emptyAuthoringField(current.fields.length),
                        blueprint,
                      ),
                      field_key: fieldKey,
                      label: candidate.raw_label,
                    };
                    fields = [...fields, created];
                    boundField = created;
                  }
                  const nextCandidate: HeaderFieldCandidateForm = {
                    ...candidate,
                    resolution: {
                      state: 'BOUND',
                      resulting_field_key: fieldKey,
                    },
                  };
                  const shouldQueueSuggested = candidate.confidence === 'MEDIUM';
                  return {
                    ...current,
                    fields,
                    header_binding_review:
                      current.header_binding_review === null
                        ? null
                        : {
                            ...current.header_binding_review,
                            candidates: current.header_binding_review.candidates.map((row) =>
                              row.candidate_id === candidate.candidate_id ? nextCandidate : row,
                            ),
                          },
                    pending_alias_actions:
                      shouldQueueSuggested && boundField !== undefined
                        ? queueSuggestedAlias(current, candidate, boundField)
                        : current.pending_alias_actions,
                  };
                });
              }}
            >
              <span aria-hidden="true">✓</span>
            </button>
            <button
              type="button"
              className="authoring-field-icon-btn"
              title="Выбрать другое поле"
              aria-label="Выбрать другое поле"
              disabled={props.disabled || props.form.fields.length < 1}
              onClick={() => {
                if (props.form.fields.length < 1) return;
                setExpandedCandidateId(candidate.candidate_id);
                setChooseOther((prev) => ({
                  ...prev,
                  [candidate.candidate_id]: {
                    selecting: true,
                    selectedKey: '',
                  },
                }));
              }}
            >
              <span aria-hidden="true">⇄</span>
            </button>
            <button
              type="button"
              className="authoring-field-icon-btn"
              title="Создать новое поле"
              aria-label="Создать новое поле"
              disabled={props.disabled}
              onClick={() => {
                props.onChange((current) => {
                  const fieldKey = `hdr_${candidate.candidate_id.slice(-12)}`;
                  const created = {
                    ...emptyAuthoringField(current.fields.length),
                    field_key: fieldKey,
                    label: candidate.raw_label,
                    binding_kind: 'CASE_DYNAMIC' as const,
                    placement: 'HEADER' as const,
                    semantic_key: `hdr.sem.${candidate.candidate_id.slice(-16)}`,
                  };
                  return {
                    ...current,
                    fields: [...current.fields, created],
                    header_binding_review:
                      current.header_binding_review === null
                        ? null
                        : {
                            ...current.header_binding_review,
                            candidates: current.header_binding_review.candidates.map((row) =>
                              row.candidate_id === candidate.candidate_id
                                ? {
                                    ...row,
                                    resolution: {
                                      state: 'CREATED_FIELD',
                                      resulting_field_key: fieldKey,
                                    },
                                  }
                                : row,
                            ),
                          },
                    pending_alias_actions: queueSuggestedAlias(current, candidate, created),
                  };
                });
              }}
            >
              <span aria-hidden="true">＋</span>
            </button>
            <button
              type="button"
              className="authoring-field-icon-btn"
              title="Игнорировать"
              aria-label="Игнорировать"
              disabled={props.disabled}
              onClick={() => {
                props.onChange((current) => {
                  const target = current.header_binding_review?.candidates.find(
                    (row) => row.candidate_id === candidate.candidate_id,
                  );
                  if (target === undefined) return current;
                  const normalizedLabel = normalizeHeaderLabel(target.normalized_label);
                  const normalizedGroup =
                    target.group_title === null ? null : normalizeHeaderLabel(target.group_title);
                  return {
                    ...current,
                    header_binding_review:
                      current.header_binding_review === null
                        ? null
                        : {
                            ...current.header_binding_review,
                            candidates: current.header_binding_review.candidates.map((row) =>
                              row.candidate_id === candidate.candidate_id
                                ? {
                                    ...row,
                                    resolution: { state: 'IGNORED', reason: 'ignored by admin' },
                                  }
                                : row,
                            ),
                          },
                    pending_alias_actions: clearPendingAliasActionsForKey(
                      current.pending_alias_actions,
                      normalizedLabel,
                      normalizedGroup,
                    ),
                  };
                });
              }}
            >
              <span aria-hidden="true">⊘</span>
            </button>
            <button
              type="button"
              className="authoring-field-icon-btn"
              title="Всегда использовать эту привязку"
              aria-label="Всегда использовать эту привязку"
              disabled={
                props.disabled ||
                (candidate.resolution.state !== 'BOUND' &&
                  candidate.resolution.state !== 'CREATED_FIELD') ||
                candidate.resolution.resulting_field_key === undefined ||
                !props.form.fields.some(
                  (field) => field.field_key === candidate.resolution.resulting_field_key,
                )
              }
              onClick={() => {
                props.onChange((current) => {
                  const bound = current.header_binding_review?.candidates.find(
                    (row) => row.candidate_id === candidate.candidate_id,
                  );
                  if (
                    bound === undefined ||
                    (bound.resolution.state !== 'BOUND' &&
                      bound.resolution.state !== 'CREATED_FIELD')
                  ) {
                    return current;
                  }
                  const field = current.fields.find(
                    (row) => row.field_key === bound.resolution.resulting_field_key,
                  );
                  if (field === undefined) return current;
                  const normalizedLabel = normalizeHeaderLabel(candidate.normalized_label);
                  const normalizedGroup =
                    candidate.group_title === null
                      ? null
                      : normalizeHeaderLabel(candidate.group_title);
                  return {
                    ...current,
                    pending_alias_actions: [
                      ...current.pending_alias_actions.filter(
                        (action) =>
                          !(
                            normalizeHeaderLabel(action.normalized_label) === normalizedLabel &&
                            (action.normalized_group === null
                              ? null
                              : normalizeHeaderLabel(action.normalized_group)) === normalizedGroup
                          ),
                      ),
                      {
                        normalized_label: normalizedLabel,
                        normalized_group: normalizedGroup,
                        status: 'TRUSTED',
                        binding_blueprint: blueprintFromField(field),
                        raw_label_examples: [candidate.raw_label],
                      },
                    ],
                  };
                });
              }}
            >
              <span aria-hidden="true">★</span>
            </button>
          </div>
        </div>
        {expanded ? (
          <div className="authoring-field-panel">
            <p className="meta">
              {review?.source_sheet} /{' '}
              {candidate.label_cells.join(', ') || `row ${candidate.source_row}`}
            </p>
            {props.form.fields.length < 1 ? (
              <p role="status" className="meta">
                Нет полей для выбора — используйте «Создать новое поле».
              </p>
            ) : null}
            {chooseState?.selecting === true ? (
              <div className="actions">
                <label>
                  Поле
                  <select
                    value={chooseState.selectedKey}
                    disabled={props.disabled}
                    onChange={(event) =>
                      setChooseOther((prev) => ({
                        ...prev,
                        [candidate.candidate_id]: {
                          selecting: true,
                          selectedKey: event.target.value,
                        },
                      }))
                    }
                  >
                    <option value="">Выберите поле</option>
                    {props.form.fields.map((field) => (
                      <option key={field.field_key} value={field.field_key}>
                        {field.label || 'Поле без названия'}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  disabled={props.disabled || chooseState.selectedKey.length === 0}
                  onClick={() => {
                    const selected = props.form.fields.find(
                      (field) => field.field_key === chooseState.selectedKey,
                    );
                    if (selected === undefined) return;
                    props.onChange((current) => {
                      const field = current.fields.find(
                        (row) => row.field_key === chooseState.selectedKey,
                      );
                      if (field === undefined) return current;
                      return {
                        ...current,
                        header_binding_review:
                          current.header_binding_review === null
                            ? null
                            : {
                                ...current.header_binding_review,
                                candidates: current.header_binding_review.candidates.map((row) =>
                                  row.candidate_id === candidate.candidate_id
                                    ? {
                                        ...row,
                                        resolution: {
                                          state: 'BOUND',
                                          resulting_field_key: field.field_key,
                                        },
                                      }
                                    : row,
                                ),
                              },
                        pending_alias_actions: queueSuggestedAlias(current, candidate, field),
                      };
                    });
                    setChooseOther((prev) => ({
                      ...prev,
                      [candidate.candidate_id]: {
                        selecting: false,
                        selectedKey: chooseState.selectedKey,
                      },
                    }));
                  }}
                >
                  Подтвердить выбор поля
                </button>
              </div>
            ) : null}
            <details className="technical">
              <summary>Технические детали</summary>
              <p className="meta">confidence: {candidate.confidence}</p>
              <p className="meta">resolution: {candidate.resolution.state}</p>
              <p className="meta">candidate_id: {candidate.candidate_id}</p>
              {candidate.resolution.resulting_field_key !== undefined ? (
                <p className="meta">field_key: {candidate.resolution.resulting_field_key}</p>
              ) : null}
              {candidate.suggested_binding !== null ? (
                <pre>{JSON.stringify(candidate.suggested_binding, null, 2)}</pre>
              ) : null}
            </details>
          </div>
        ) : null}
      </li>
    );
  };

  return (
    <>
      {review !== null ? (
        <section
          ref={headerCandidatesRegionRef}
          aria-label="Найденные поля шапки"
          className="form-section"
        >
          <h3>Найденные поля шапки</h3>
          <p role="status">
            найдено {review.candidates.length}; разрешено {resolvedCount}; требует решения{' '}
            {unresolvedCount}
          </p>
          {props.form.alias_learning_error !== null ? (
            <>
              <p role="alert" className="banner banner-conflict">
                Обучение алиаса не сохранено: {props.form.alias_learning_error}
              </p>
              <div className="actions">
                <button
                  type="button"
                  disabled={
                    props.disabled ||
                    props.form.pending_alias_actions.length < 1 ||
                    props.onRetryAliasLearning === undefined
                  }
                  onClick={() => props.onRetryAliasLearning?.()}
                >
                  Повторить сохранение привязок
                </button>
              </div>
            </>
          ) : null}
          <div className="actions">
            <button
              type="button"
              disabled={props.disabled}
              onClick={() => {
                props.onChange((current) => {
                  if (current.header_binding_review === null) return current;
                  let fields = [...current.fields];
                  const candidates = current.header_binding_review.candidates.map((candidate) => {
                    if (
                      candidate.resolution.state !== 'UNRESOLVED' ||
                      candidate.confidence !== 'HIGH' ||
                      candidate.suggested_binding === null
                    ) {
                      return candidate;
                    }
                    const blueprint = candidate.suggested_binding;
                    const existing = findEquivalentField(
                      fields,
                      normalizeHeaderLabel(candidate.raw_label),
                      blueprint,
                    );
                    const fieldKey =
                      existing?.field_key ?? `hdr_${candidate.candidate_id.slice(-12)}`;
                    if (existing === undefined) {
                      const created = {
                        ...applyBindingBlueprintToField(
                          emptyAuthoringField(fields.length),
                          blueprint,
                        ),
                        field_key: fieldKey,
                        label: candidate.raw_label,
                      };
                      fields = [...fields, created];
                    }
                    return {
                      ...candidate,
                      resolution: { state: 'BOUND' as const, resulting_field_key: fieldKey },
                    };
                  });
                  return {
                    ...current,
                    fields,
                    header_binding_review: { ...current.header_binding_review, candidates },
                  };
                });
              }}
            >
              Принять все точные совпадения
            </button>
          </div>
          {review.groups.map((group) => {
            const groupCandidates = review.candidates.filter(
              (candidate) => candidate.group_id === group.candidate_id,
            );
            return (
              <div key={group.candidate_id} className="authoring-candidate-group">
                <label>
                  Группа
                  <input
                    value={group.raw_title}
                    disabled={props.disabled}
                    onChange={(event) => {
                      const rawTitle = event.target.value;
                      const normalizedTitle = normalizeHeaderLabel(rawTitle);
                      const oldNormalizedTitle = group.normalized_title;
                      props.onChange((current) => {
                        if (current.header_binding_review === null) return current;
                        return {
                          ...current,
                          header_binding_review: {
                            ...current.header_binding_review,
                            groups: current.header_binding_review.groups.map((row) =>
                              row.candidate_id === group.candidate_id
                                ? {
                                    ...row,
                                    raw_title: rawTitle,
                                    normalized_title: normalizedTitle,
                                  }
                                : row,
                            ),
                            candidates: current.header_binding_review.candidates.map((row) =>
                              row.group_id === group.candidate_id
                                ? { ...row, group_title: rawTitle }
                                : row,
                            ),
                          },
                          pending_alias_actions: rekeyPendingAliasActionsForGroup(
                            current.pending_alias_actions,
                            oldNormalizedTitle,
                            normalizedTitle,
                          ),
                        };
                      });
                    }}
                  />
                </label>
                <ul className="authoring-field-list" aria-label={`Кандидаты группы ${group.raw_title}`}>
                  {groupCandidates.map((candidate) => renderCandidateCard(candidate))}
                </ul>
              </div>
            );
          })}
          {(() => {
            const knownGroupIds = new Set(review.groups.map((group) => group.candidate_id));
            const ungrouped = review.candidates.filter(
              (candidate) => candidate.group_id === null || !knownGroupIds.has(candidate.group_id),
            );
            if (ungrouped.length === 0) return null;
            return (
              <div className="authoring-candidate-group">
                <p className="meta">Без группы</p>
                <ul className="authoring-field-list" aria-label="Кандидаты без группы">
                  {ungrouped.map((candidate) => renderCandidateCard(candidate))}
                </ul>
              </div>
            );
          })()}
        </section>
      ) : null}
      <div className="actions">
        <button
          type="button"
          disabled={props.disabled}
          aria-expanded={props.composerOpen}
          onClick={() => {
            if (props.composerOpen) {
              closeAddPanel();
              return;
            }
            setComposerError(null);
            props.onComposerOpenChange(true);
          }}
        >
          Добавить поле
        </button>
      </div>
      {props.composerOpen ? (
        <div className="authoring-field-add-panel" role="region" aria-label="Добавление поля">
          <p className="authoring-field-add-hint">{FIELD_REUSE_ADD_HINT}</p>
          <label htmlFor="tad_field_reuse_label">
            Название поля
            <input
              ref={addLabelInputRef}
              id="tad_field_reuse_label"
              value={addLabel}
              disabled={props.disabled}
              maxLength={120}
              onChange={(event) => {
                setAddLabel(event.target.value);
                if (composerError !== null) {
                  setComposerError(null);
                }
              }}
            />
          </label>
          {composerError !== null ? (
            <p className="authoring-field-reuse-row-error" role="alert">
              {composerError}
            </p>
          ) : null}
          {searchState === 'loading' ? (
            <p className="authoring-field-reuse-status" role="status">
              Поиск похожих полей…
            </p>
          ) : null}
          {searchState === 'error' ? (
            <p className="authoring-field-reuse-status" role="status">
              Не удалось загрузить похожие поля
            </p>
          ) : null}
          {searchState === 'empty' ? (
            <p className="authoring-field-reuse-status" role="status">
              Похожих общих полей не найдено
            </p>
          ) : null}
          {searchState === 'results' ? (
            <ul className="authoring-field-reuse-list">
              {candidates.map((candidate) => {
                const used = fieldReuseUsedInSummary(candidate.used_in);
                const block = fieldReuseLinkBlockReason(candidate, props.form.fields);
                const error = linkRowError[candidate.semantic_key] ?? null;
                const disabledLink = props.disabled || block !== null;
                return (
                  <li key={candidate.semantic_key} className="authoring-field-reuse-row">
                    <div className="authoring-field-reuse-row-main">
                      <span className="authoring-field-reuse-label" title={candidate.label}>
                        {candidate.label}
                      </span>
                      <p className="authoring-field-reuse-used" title={used}>
                        {used.length > 0 ? `Используется: ${used}` : 'Используется: —'}
                      </p>
                      {error !== null ? (
                        <p className="authoring-field-reuse-row-error" role="status">
                          {error}
                        </p>
                      ) : null}
                      {block !== null && error === null ? (
                        <p className="authoring-field-reuse-row-error" role="status">
                          {block}
                        </p>
                      ) : null}
                    </div>
                    <div className="authoring-field-reuse-row-action">
                      <button
                        type="button"
                        className="authoring-field-reuse-link-btn"
                        disabled={disabledLink}
                        title={
                          block !== null
                            ? block
                            : `Добавить и связать «${candidate.label}»`
                        }
                        aria-label={
                          block !== null
                            ? `Нельзя связать: ${block}`
                            : `Добавить и связать «${candidate.label}»`
                        }
                        onClick={() => {
                          const built = authoringFieldFromReuseCandidate(
                            candidate,
                            props.form.fields,
                          );
                          if ('error' in built) {
                            setLinkRowError((current) => ({
                              ...current,
                              [candidate.semantic_key]: built.error,
                            }));
                            return;
                          }
                          appendFieldAndFocus(built);
                        }}
                      >
                        <span className="authoring-field-reuse-link-text">Добавить и связать</span>
                        <span aria-hidden="true">⛓</span>
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : null}
          <div className="authoring-field-reuse-create authoring-field-composer-actions">
            <button
              type="button"
              className="authoring-field-reuse-create-btn"
              disabled={props.disabled}
              onClick={() => {
                if (addLabel.trim().length < 1) {
                  setComposerError('Укажите название поля, чтобы создать его.');
                  return;
                }
                const created = createUnlinkedCaseDynamicField(addLabel, props.form.fields);
                appendFieldAndFocus(created);
              }}
            >
              Создать поле
            </button>
            <button
              type="button"
              disabled={props.disabled}
              onClick={() => {
                closeAddPanel();
              }}
            >
              Отмена
            </button>
            <p className="authoring-field-reuse-create-caption">{FIELD_REUSE_CREATE_NEW_CAPTION}</p>
          </div>
        </div>
      ) : null}
      <div
        className="authoring-field-mode"
        role="group"
        aria-label="Режим редактирования полей предзаполнения"
      >
        <button
          type="button"
          className={
            fieldUiMode === 'constructor'
              ? 'authoring-field-mode-btn authoring-field-mode-btn-active'
              : 'authoring-field-mode-btn'
          }
          aria-pressed={fieldUiMode === 'constructor'}
          onClick={() => setFieldUiMode('constructor')}
        >
          Конструктор
        </button>
        <button
          type="button"
          className={
            fieldUiMode === 'advanced'
              ? 'authoring-field-mode-btn authoring-field-mode-btn-active'
              : 'authoring-field-mode-btn'
          }
          aria-pressed={fieldUiMode === 'advanced'}
          onClick={() => setFieldUiMode('advanced')}
        >
          Доп. настройки
        </button>
      </div>
      {props.form.fields.length === 0 ? (
        <p role="status" className="banner">
          Поля ещё не добавлены.
        </p>
      ) : null}
      <ul className="authoring-field-list">
        {props.form.fields.map((field, index) => {
          const fieldId = authoringFieldRowId(field, index);
          const expanded = expandedFieldId === fieldId;
          const linked =
            linkedLookupReady && field.binding_kind === 'CASE_DYNAMIC'
              ? (linkedByKey.get(field.semantic_key.trim()) ?? null)
              : null;
          return (
            <FieldEditor
              key={`${fieldId}#${index}`}
              index={index}
              field={field}
              fieldId={fieldId}
              mode={fieldUiMode}
              expanded={expanded}
              focusLabel={focusLabelFieldId === fieldId}
              canMoveUp={index > 0}
              canMoveDown={index < props.form.fields.length - 1}
              siblingSemanticKeys={props.form.fields
                .filter((_, rowIndex) => rowIndex !== index)
                .map((row) => row.semantic_key)}
              linkedExternal={linked}
              usagePopoverOpen={usagePopoverFieldId === fieldId}
              identityEditHint={identityEditHintFieldId === fieldId}
              disabled={props.disabled}
              onChange={(next) => {
                if (
                  linked !== null &&
                  fieldReuseIdentityCriticalChanged(field, next)
                ) {
                  setIdentityEditHintFieldId(fieldId);
                  return;
                }
                if (identityEditHintFieldId === fieldId) {
                  setIdentityEditHintFieldId(null);
                }
                props.onChange((current) => ({
                  ...current,
                  fields: current.fields.map((row, rowIndex) => (rowIndex === index ? next : row)),
                }));
              }}
              onToggleExpand={() => {
                setUsagePopoverFieldId(null);
                setExpandedFieldId((current) => (current === fieldId ? null : fieldId));
              }}
              onEdit={() => {
                setUsagePopoverFieldId(null);
                setExpandedFieldId(fieldId);
                setFocusLabelFieldId(fieldId);
              }}
              onToggleUsagePopover={() =>
                setUsagePopoverFieldId((current) => (current === fieldId ? null : fieldId))
              }
              onCloseUsagePopover={() => setUsagePopoverFieldId(null)}
              onFocusLabelHandled={() => setFocusLabelFieldId(null)}
              onUnlink={() => {
                if (linked === null) {
                  return;
                }
                if (
                  !window.confirm(
                    'Разорвать связь? Поле останется в этом черновике, но станет независимым.',
                  )
                ) {
                  return;
                }
                setUsagePopoverFieldId(null);
                setIdentityEditHintFieldId(null);
                props.onChange((current) => ({
                  ...current,
                  fields: current.fields.map((row, rowIndex) => {
                    if (rowIndex !== index) {
                      return row;
                    }
                    return forceUnlinkCaseDynamicSemanticKey(
                      row,
                      current.fields
                        .filter((_, other) => other !== index)
                        .map((other) => other.semantic_key),
                    );
                  }),
                }));
              }}
              onRemove={() => {
                const label = field.label.trim().length > 0 ? field.label.trim() : `Поле ${index + 1}`;
                if (!window.confirm(`Удалить поле «${label}»?`)) {
                  return;
                }
                setUsagePopoverFieldId(null);
                if (expandedFieldId === fieldId) {
                  setExpandedFieldId(null);
                }
                props.onChange((current) => ({
                  ...current,
                  fields: reindexOrdinals(
                    current.fields.filter((_, rowIndex) => rowIndex !== index),
                  ),
                }));
              }}
              onMoveUp={() => {
                props.onChange((current) => ({
                  ...current,
                  fields: reindexOrdinals(moveItem(current.fields, index, index - 1)),
                }));
              }}
              onMoveDown={() => {
                props.onChange((current) => ({
                  ...current,
                  fields: reindexOrdinals(moveItem(current.fields, index, index + 1)),
                }));
              }}
            />
          );
        })}
      </ul>
    </>
  );
}

function authoringFieldRowId(field: AuthoringFieldForm, index: number): string {
  const key = field.field_key.trim();
  return key.length > 0 ? `fk:${key}` : `idx:${index}`;
}

function fieldHasAdvancedIndicators(field: AuthoringFieldForm): boolean {
  if (field.applicability_rule.type !== 'ALWAYS') {
    return true;
  }
  if (field.binding_kind !== 'CHECKLIST_DYNAMIC') {
    return true;
  }
  if (field.placement !== 'OBJECT_BLOCK') {
    return true;
  }
  if (field.pdf_label.trim().length > 0) {
    return true;
  }
  if (field.semantic_key.trim().length > 0) {
    return true;
  }
  if (field.snapshot_path.trim().length > 0 || field.snapshot_data_key.trim().length > 0) {
    return true;
  }
  if (!isBlankValidation(field.validation) && Object.keys(field.validation).length > 1) {
    return true;
  }
  return false;
}

function FieldEditor(props: {
  readonly index: number;
  readonly field: AuthoringFieldForm;
  readonly fieldId: string;
  readonly mode: 'constructor' | 'advanced';
  readonly expanded: boolean;
  readonly focusLabel: boolean;
  readonly canMoveUp: boolean;
  readonly canMoveDown: boolean;
  readonly siblingSemanticKeys: readonly string[];
  readonly linkedExternal: TemplateFieldReuseCandidate | null;
  readonly usagePopoverOpen: boolean;
  readonly identityEditHint: boolean;
  readonly disabled: boolean;
  readonly onChange: (field: AuthoringFieldForm) => void;
  readonly onRemove: () => void;
  readonly onMoveUp: () => void;
  readonly onMoveDown: () => void;
  readonly onToggleExpand: () => void;
  readonly onEdit: () => void;
  readonly onToggleUsagePopover: () => void;
  readonly onCloseUsagePopover: () => void;
  readonly onUnlink: () => void;
  readonly onFocusLabelHandled: () => void;
}): ReactElement {
  const field = props.field;
  const labelInputRef = useRef<HTMLInputElement | null>(null);
  const panelId = `tad_field_panel_${props.fieldId}`;
  const displayLabel =
    field.label.trim().length > 0 ? field.label.trim() : `Поле ${props.index + 1}`;
  const advanced = fieldHasAdvancedIndicators(field);
  const bindingHint = authoringBindingKindHint(field.binding_kind);
  const valueTypeHint = authoringValueTypeHint(field.value_type);
  const linked = props.linkedExternal;
  const linkedStatus =
    linked !== null ? fieldReuseLinkedStatusLine(linked.used_in) : null;
  const resolvedSnapshotPath =
    field.binding_kind === 'CASE_SNAPSHOT' ? (resolveSnapshotPath(field) ?? '') : '';
  const masterRole = field.master_object_role.trim();
  const masterAttributeOptions = masterAttributesForObjectRole(masterRole);

  useEffect(() => {
    if (!props.focusLabel || !props.expanded) {
      return;
    }
    const input = labelInputRef.current;
    if (input === null) {
      return;
    }
    input.focus();
    input.select();
    props.onFocusLabelHandled();
    // Intentionally depend only on focus request flags; handler identity is unstable.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- focus once per request
  }, [props.focusLabel, props.expanded]);

  return (
    <li
      className={
        props.expanded ? 'authoring-field-row authoring-field-row-expanded' : 'authoring-field-row'
      }
    >
      <div className="authoring-field-row-main">
        <div className="authoring-field-summary">
          <div className="authoring-field-linked-title">
            {linked !== null ? (
              <span className="authoring-field-link-indicator">
                <button
                  type="button"
                  className="authoring-field-link-chip"
                  disabled={props.disabled}
                  title={FIELD_REUSE_LINKED_HINT}
                  aria-label={`Где используется «${displayLabel}»`}
                  aria-expanded={props.usagePopoverOpen}
                  aria-haspopup="dialog"
                  onClick={props.onToggleUsagePopover}
                >
                  <span aria-hidden="true">⛓</span>
                </button>
                {props.usagePopoverOpen ? (
                  <div
                    className="authoring-field-usage-popover"
                    role="dialog"
                    aria-label={`Использование «${displayLabel}»`}
                  >
                    <p className="meta">{FIELD_REUSE_LINKED_HINT}</p>
                    <ul>
                      {linked.used_in.map((row) => {
                        const label = fieldReuseDisplayTemplateLabel(row);
                        const detail =
                          row.template_code.trim().length > 0
                            ? row.template_code.trim()
                            : row.template_name.trim();
                        return (
                          <li key={row.checklist_template_id} title={detail}>
                            {label}
                          </li>
                        );
                      })}
                    </ul>
                    <button type="button" onClick={props.onCloseUsagePopover}>
                      Закрыть
                    </button>
                  </div>
                ) : null}
              </span>
            ) : null}
            <strong className="authoring-field-title" title={displayLabel}>
              {displayLabel}
            </strong>
          </div>
          <p className="meta authoring-field-summary-line" title={linkedStatus ?? undefined}>
            {linkedStatus !== null ? (
              linkedStatus
            ) : (
              <>
                {authoringValueTypeLabel(field.value_type)}
                {' · '}
                Кто заполняет: {authoringOwnerLabel(field.owner_code)}
                {' · '}
                Нужно: {authoringRequiredRuleLabel(field.required_rule.type)}
                {' · '}
                {authoringBindingKindLabel(field.binding_kind)}
                {advanced ? (
                  <>
                    {' · '}
                    <span
                      className="authoring-field-badge"
                      title="Есть дополнительные или нестандартные настройки"
                    >
                      доп.
                    </span>
                  </>
                ) : null}
              </>
            )}
          </p>
          {props.identityEditHint ? (
            <p className="meta authoring-field-reuse-row-error" role="status">
              {FIELD_REUSE_IDENTITY_EDIT_HINT}
            </p>
          ) : null}
        </div>
        <div className="authoring-field-actions">
          <button
            type="button"
            className="authoring-field-icon-btn"
            disabled={props.disabled}
            title={`Изменить поле «${displayLabel}»`}
            aria-label={`Изменить поле «${displayLabel}»`}
            onClick={props.onEdit}
          >
            <span aria-hidden="true">✎</span>
          </button>
          <button
            type="button"
            className="authoring-field-icon-btn"
            disabled={props.disabled || !props.canMoveUp}
            title="Выше"
            aria-label={`Выше: переместить поле «${displayLabel}»`}
            onClick={props.onMoveUp}
          >
            <span aria-hidden="true">↑</span>
          </button>
          <button
            type="button"
            className="authoring-field-icon-btn"
            disabled={props.disabled || !props.canMoveDown}
            title="Ниже"
            aria-label={`Ниже: переместить поле «${displayLabel}»`}
            onClick={props.onMoveDown}
          >
            <span aria-hidden="true">↓</span>
          </button>
          <button
            type="button"
            className="authoring-field-icon-btn"
            disabled={props.disabled}
            title="Удалить"
            aria-label={`Удалить поле «${displayLabel}»`}
            onClick={props.onRemove}
          >
            <span aria-hidden="true">🗑</span>
          </button>
          {linked !== null ? (
            <button
              type="button"
              className="authoring-field-icon-btn authoring-field-unlink-btn"
              disabled={props.disabled}
              title={`Разорвать связь «${displayLabel}»`}
              aria-label={`Разорвать связь «${displayLabel}»`}
              onClick={props.onUnlink}
            >
              <span aria-hidden="true">⊘</span>
              <span className="authoring-field-unlink-label">Разорвать связь</span>
            </button>
          ) : null}
          <button
            type="button"
            className="authoring-field-icon-btn"
            disabled={props.disabled}
            title={
              props.expanded
                ? `Свернуть настройки «${displayLabel}»`
                : `Раскрыть настройки «${displayLabel}»`
            }
            aria-label={
              props.expanded
                ? `Свернуть настройки «${displayLabel}»`
                : `Раскрыть настройки «${displayLabel}»`
            }
            aria-expanded={props.expanded}
            aria-controls={panelId}
            onClick={props.onToggleExpand}
          >
            <span aria-hidden="true">{props.expanded ? 'v' : '>'}</span>
          </button>
        </div>
      </div>
      {props.expanded ? (
        <div
          id={panelId}
          className="authoring-field-panel"
          role="region"
          aria-label={`Настройки поля «${displayLabel}»`}
        >
          <section className="authoring-field-section">
            <h4 className="authoring-field-section-title">Основное</h4>
            <label htmlFor={`tad_field_label_${props.index}`}>
              Название поля
              <input
                ref={labelInputRef}
                id={`tad_field_label_${props.index}`}
                value={field.label}
                disabled={props.disabled}
                onChange={(event) => props.onChange({ ...field, label: event.target.value })}
              />
            </label>
            <label htmlFor={`tad_field_value_type_${props.index}`}>
              Тип поля
              <select
                id={`tad_field_value_type_${props.index}`}
                value={field.value_type}
                disabled={props.disabled}
                title={valueTypeHint}
                onChange={(event) => {
                  const nextType = event.target.value as AuthoringFieldForm['value_type'];
                  const result = validationAfterValueTypeChange(field.validation, nextType);
                  if (result.blocked) {
                    window.alert(
                      'Сначала преобразуйте историческое правило проверки значения, затем меняйте тип поля.',
                    );
                    return;
                  }
                  props.onChange({
                    ...field,
                    value_type: nextType,
                    validation: result.validation,
                  });
                }}
              >
                {AUTHORING_FIELD_VALUE_TYPES.map((type) => (
                  <option key={type} value={type} title={authoringValueTypeHint(type)}>
                    {authoringValueTypeLabel(type)}
                  </option>
                ))}
              </select>
            </label>
            <label htmlFor={`tad_field_owner_${props.index}`}>
              Кто заполняет
              <select
                id={`tad_field_owner_${props.index}`}
                value={field.owner_code}
                disabled={props.disabled}
                title="Кто заполняет: подрядчик, инспектор или система."
                onChange={(event) =>
                  props.onChange({
                    ...field,
                    owner_code: event.target.value as AuthoringFieldForm['owner_code'],
                  })
                }
              >
                {AUTHORING_OWNER_CODES.map((owner) => (
                  <option key={owner} value={owner}>
                    {authoringOwnerLabel(owner)}
                  </option>
                ))}
              </select>
            </label>
          </section>

          {props.mode === 'constructor' ? (
            <section className="authoring-field-section">
              <RequiredRuleEditor
                id={`tad_field_required_${props.index}`}
                value={field.required_rule}
                disabled={props.disabled}
                onChange={(required_rule) => props.onChange({ ...field, required_rule })}
              />
            </section>
          ) : null}

          {props.mode === 'advanced' ? (
            <>
              <section className="authoring-field-section">
                <h4 className="authoring-field-section-title">Источник и размещение</h4>
                <label htmlFor={`tad_field_binding_${props.index}`}>
                  Источник данных
                  <select
                    id={`tad_field_binding_${props.index}`}
                    value={field.binding_kind}
                    disabled={props.disabled}
                    title={bindingHint}
                    onChange={(event) =>
                      props.onChange(
                        withAuthoringBindingKind(
                          field,
                          event.target.value as AuthoringFieldForm['binding_kind'],
                          props.siblingSemanticKeys,
                        ),
                      )
                    }
                  >
                    {AUTHORING_BINDING_KINDS.map((kind) => (
                      <option key={kind} value={kind} title={authoringBindingKindHint(kind)}>
                        {authoringBindingKindLabel(kind)}
                      </option>
                    ))}
                  </select>
                </label>
                {field.binding_kind === 'CASE_DYNAMIC' ? (
                  <p className="meta" tabIndex={0} title={bindingHint}>
                    Общее поле осмотра — значение используется на уровне всего осмотра.
                  </p>
                ) : null}
                {field.binding_kind === 'CASE_SNAPSHOT' ? (
                  <>
                    <label htmlFor={`tad_field_snap_family_${props.index}`}>
                      Семейство пути в сведениях осмотра
                      <select
                        id={`tad_field_snap_family_${props.index}`}
                        value={field.snapshot_family}
                        disabled={props.disabled}
                        onChange={(event) =>
                          props.onChange({
                            ...field,
                            snapshot_family: event.target
                              .value as AuthoringFieldForm['snapshot_family'],
                          })
                        }
                      >
                        {SNAPSHOT_PATH_FAMILIES.map((family) => (
                          <option key={family} value={family}>
                            {authoringSnapshotFamilyLabel(family)}
                          </option>
                        ))}
                      </select>
                    </label>
                    {field.snapshot_family === 'STATIC' ? (
                      <label htmlFor={`tad_field_snap_static_${props.index}`}>
                        Путь в сведениях осмотра
                        <select
                          id={`tad_field_snap_static_${props.index}`}
                          value={field.snapshot_path}
                          disabled={props.disabled}
                          onChange={(event) =>
                            props.onChange({ ...field, snapshot_path: event.target.value })
                          }
                        >
                          <option value="">Выберите путь</option>
                          {STATIC_SNAPSHOT_PATHS.map((path) => (
                            <option key={path} value={path}>
                              {path}
                            </option>
                          ))}
                        </select>
                      </label>
                    ) : null}
                    {field.snapshot_family === 'VEHICLE_DATA' ||
                    field.snapshot_family === 'CONTRACTOR_DATA' ||
                    field.snapshot_family === 'PERSON_DATA' ? (
                      <label htmlFor={`tad_field_snap_key_${props.index}`}>
                        Ключ данных
                        <input
                          id={`tad_field_snap_key_${props.index}`}
                          value={field.snapshot_data_key}
                          disabled={props.disabled}
                          onChange={(event) =>
                            props.onChange({ ...field, snapshot_data_key: event.target.value })
                          }
                        />
                      </label>
                    ) : null}
                    {field.snapshot_family === 'PERSON_FULL_NAME' ||
                    field.snapshot_family === 'PERSON_DATA' ? (
                      <label htmlFor={`tad_field_snap_person_${props.index}`}>
                        Идентификатор лица
                        <input
                          id={`tad_field_snap_person_${props.index}`}
                          value={field.snapshot_person_id}
                          disabled={props.disabled}
                          onChange={(event) =>
                            props.onChange({ ...field, snapshot_person_id: event.target.value })
                          }
                        />
                      </label>
                    ) : null}
                  </>
                ) : null}
                <label htmlFor={`tad_field_placement_${props.index}`}>
                  Где показывать
                  <select
                    id={`tad_field_placement_${props.index}`}
                    value={field.placement}
                    disabled={props.disabled}
                    title={AUTHORING_PLACEMENT_HINT}
                    onChange={(event) =>
                      props.onChange({
                        ...field,
                        placement: event.target.value as AuthoringFieldForm['placement'],
                      })
                    }
                  >
                    {AUTHORING_PLACEMENTS.map((placement) => (
                      <option key={placement} value={placement}>
                        {authoringPlacementLabel(placement)}
                      </option>
                    ))}
                  </select>
                </label>
                {resolvedSnapshotPath.trim().length > 0 ? (
                  <p className="meta" tabIndex={0}>
                    Текущий путь в сведениях осмотра (только справка): {resolvedSnapshotPath}
                  </p>
                ) : null}
                <label htmlFor={`tad_field_master_role_${props.index}`}>
                  Бизнес-назначение
                  <select
                    id={`tad_field_master_role_${props.index}`}
                    value={field.master_object_role}
                    disabled={props.disabled}
                    title="Явная привязка к основному профилю подрядчика или ТС. Не выводится из подписи или пути."
                    onChange={(event) => {
                      const nextRole = event.target.value;
                      const allowed = masterAttributesForObjectRole(nextRole);
                      const nextAttr =
                        allowed.length === 1
                          ? allowed[0]!
                          : allowed.includes(field.master_attribute as (typeof allowed)[number])
                            ? field.master_attribute
                            : '';
                      props.onChange({
                        ...field,
                        master_object_role: nextRole,
                        master_attribute: nextAttr,
                      });
                    }}
                  >
                    <option value="">{authoringMasterObjectRoleLabel('')}</option>
                    <option value="PRIMARY_CONTRACTOR">
                      {authoringMasterObjectRoleLabel('PRIMARY_CONTRACTOR')}
                    </option>
                    <option value="PRIMARY_VEHICLE">
                      {authoringMasterObjectRoleLabel('PRIMARY_VEHICLE')}
                    </option>
                  </select>
                </label>
                {masterRole.length > 0 ? (
                  <label htmlFor={`tad_field_master_attr_${props.index}`}>
                    Атрибут профиля
                    <select
                      id={`tad_field_master_attr_${props.index}`}
                      value={field.master_attribute}
                      disabled={props.disabled}
                      onChange={(event) =>
                        props.onChange({ ...field, master_attribute: event.target.value })
                      }
                    >
                      <option value="">{authoringMasterAttributeLabel('')}</option>
                      {masterAttributeOptions.map((attribute) => (
                        <option key={attribute} value={attribute}>
                          {authoringMasterAttributeLabel(attribute)}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                <p className="meta" tabIndex={0}>
                  Настройки бизнес-назначения влияют только на новые подтверждённые бронирования после
                  публикации шаблона; уже созданные брони не пересчитываются автоматически.
                </p>
              </section>

              <section className="authoring-field-section">
                <h4 className="authoring-field-section-title">Условия поля</h4>
                <ApplicabilityRuleEditor
                  id={`tad_field_applicability_${props.index}`}
                  value={field.applicability_rule}
                  disabled={props.disabled}
                  showContractorWarning={field.owner_code === 'CONTRACTOR'}
                  onChange={(applicability_rule) =>
                    props.onChange({ ...field, applicability_rule })
                  }
                />
                <RequiredRuleEditor
                  id={`tad_field_required_${props.index}`}
                  value={field.required_rule}
                  disabled={props.disabled}
                  onChange={(required_rule) => props.onChange({ ...field, required_rule })}
                />
              </section>

              <section className="authoring-field-section">
                <h4 className="authoring-field-section-title">Проверка значения</h4>
                <CfgValValidationEditor
                  valueType={field.value_type}
                  validation={field.validation}
                  disabled={props.disabled}
                  ariaPrefix={`поле ${props.index + 1}`}
                  masterAttribute={field.master_attribute}
                  onChange={(validation) => props.onChange({ ...field, validation })}
                />
              </section>

              <section className="authoring-field-section">
                <h4 className="authoring-field-section-title">PDF</h4>
                <label htmlFor={`tad_field_pdf_${props.index}`}>
                  Название для PDF
                  <input
                    id={`tad_field_pdf_${props.index}`}
                    value={field.pdf_label}
                    disabled={props.disabled}
                    title={AUTHORING_PDF_METADATA_LABEL_HINT}
                    onChange={(event) =>
                      props.onChange({ ...field, pdf_label: event.target.value })
                    }
                  />
                </label>
              </section>
            </>
          ) : null}

          <div className="authoring-field-panel-footer">
            <button
              type="button"
              className="authoring-field-collapse-btn"
              disabled={props.disabled}
              onClick={props.onToggleExpand}
            >
              Свернуть
            </button>
          </div>
        </div>
      ) : null}
    </li>
  );
}

function DocumentsArea(props: {
  readonly form: AuthoringPayloadForm;
  readonly documentTypes: DocumentTypeAdmin[] | null;
  readonly disabled: boolean;
  readonly unresolvedSectionRef: RefObject<HTMLElement | null>;
  readonly api: AdminApi;
  readonly onChange: (updater: (current: AuthoringPayloadForm) => AuthoringPayloadForm) => void;
  readonly onDocumentTypesChanged: (rows: DocumentTypeAdmin[]) => void;
  readonly onBusyError: (failure: unknown) => void;
  readonly onBusySuccess: (message: string) => void;
  readonly setBusy: (busy: boolean) => void;
}): ReactElement {
  const proposals = props.form.import_evidence?.proposed_documents ?? [];
  return (
    <>
      {proposals.length > 0 ? (
        <section
          ref={props.unresolvedSectionRef as RefObject<HTMLElement>}
          tabIndex={-1}
          aria-label="Документы, найденные при импорте"
          className="stack-gap"
        >
          <h3>Документы, найденные при импорте</h3>
          <p className="meta">
            Эти документы обнаружены при импорте Excel и ещё не связаны с типом из справочника.
            Выберите существующий тип, создайте и привяжите тип, либо явно исключите документ из
            этого черновика.
          </p>
          {proposals.map((proposal) => (
            <ImportProposalCard
              key={proposal.client_key}
              proposal={proposal}
              documentTypes={props.documentTypes}
              disabled={props.disabled}
              api={props.api}
              onBindExisting={(documentTypeId) => {
                const selected = (props.documentTypes ?? []).find(
                  (row) => row.id === documentTypeId,
                );
                props.onChange((current) =>
                  bindImportProposalToDocumentType(current, proposal.client_key, documentTypeId, {
                    defaultMetadataSchema: selected?.default_metadata_schema,
                  }),
                );
                props.onBusySuccess('Документ привязан к существующему типу.');
              }}
              onCreatedAndBound={(documentType) => {
                props.onDocumentTypesChanged([
                  ...(props.documentTypes ?? []).filter((row) => row.id !== documentType.id),
                  documentType,
                ]);
                props.onChange((current) =>
                  bindImportProposalToDocumentType(current, proposal.client_key, documentType.id, {
                    defaultMetadataSchema: documentType.default_metadata_schema,
                  }),
                );
                props.onBusySuccess('Тип документа создан и привязан к требованию.');
              }}
              onExclude={() => {
                props.onChange((current) => excludeImportProposal(current, proposal.client_key));
                props.onBusySuccess('Документ исключён из этого черновика.');
              }}
              onError={props.onBusyError}
              setBusy={props.setBusy}
            />
          ))}
        </section>
      ) : null}
      <div className="actions">
        <button
          type="button"
          disabled={props.disabled}
          onClick={() =>
            props.onChange((current) => ({
              ...current,
              document_requirements: reindexOrdinals([
                ...current.document_requirements,
                emptyDocumentRequirement(current.document_requirements.length),
              ]),
            }))
          }
        >
          Добавить требование к документу
        </button>
      </div>
      {props.form.document_requirements.length === 0 ? (
        <p role="status" className="banner">
          {proposals.length > 0
            ? 'Связанные требования к документам ещё не добавлены — обработайте найденные при импорте документы выше или добавьте требование вручную.'
            : 'Требования к документам ещё не добавлены.'}
        </p>
      ) : null}
      {props.form.document_requirements.map((document, index) => (
        <DocumentEditor
          key={`doc-${index}`}
          index={index}
          document={document}
          documentTypes={props.documentTypes}
          disabled={props.disabled}
          onChange={(next) =>
            props.onChange((current) => ({
              ...current,
              document_requirements: current.document_requirements.map((row, rowIndex) =>
                rowIndex === index ? next : row,
              ),
            }))
          }
          onRemove={() =>
            props.onChange((current) => ({
              ...current,
              document_requirements: reindexOrdinals(
                current.document_requirements.filter((_, rowIndex) => rowIndex !== index),
              ),
            }))
          }
          onMoveUp={() =>
            props.onChange((current) => ({
              ...current,
              document_requirements: reindexOrdinals(
                moveItem(current.document_requirements, index, index - 1),
              ),
            }))
          }
          onMoveDown={() =>
            props.onChange((current) => ({
              ...current,
              document_requirements: reindexOrdinals(
                moveItem(current.document_requirements, index, index + 1),
              ),
            }))
          }
        />
      ))}
    </>
  );
}

function ImportProposalCard(props: {
  readonly proposal: ImportProposedDocumentForm;
  readonly documentTypes: DocumentTypeAdmin[] | null;
  readonly disabled: boolean;
  readonly api: AdminApi;
  readonly onBindExisting: (documentTypeId: string) => void;
  readonly onCreatedAndBound: (documentType: DocumentTypeAdmin) => void;
  readonly onExclude: () => void;
  readonly onError: (failure: unknown) => void;
  readonly setBusy: (busy: boolean) => void;
}): ReactElement {
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [confirmExclude, setConfirmExclude] = useState(false);
  const knownCode =
    props.proposal.document_type_code !== null &&
    props.proposal.document_type_code.trim().length > 0
      ? props.proposal.document_type_code.trim()
      : null;
  const captureLabel =
    props.proposal.capture_mode !== null && props.proposal.capture_mode.trim().length > 0
      ? authoringCaptureModeLabel(props.proposal.capture_mode)
      : 'Не определён';

  const createAndBind = () => {
    props.setBusy(true);
    void (async () => {
      try {
        let catalog =
          props.documentTypes ?? (await props.api.listDocumentTypes({ status: 'ACTIVE' }));
        if (knownCode !== null) {
          const existing = catalog.find((row) => row.code === knownCode && row.status === 'ACTIVE');
          if (existing !== undefined) {
            props.onBindExisting(existing.id);
            return;
          }
          try {
            const created = await props.api.createDocumentType({
              code: knownCode,
              display_name:
                props.proposal.display_label.trim().length > 0
                  ? props.proposal.display_label.trim()
                  : knownCode,
              default_metadata_schema: emptyMetadataSchema(),
            });
            props.onCreatedAndBound(created);
            return;
          } catch (failure: unknown) {
            catalog = await props.api.listDocumentTypes({ status: 'ACTIVE' });
            const afterConflict = catalog.find((row) => row.code === knownCode);
            if (afterConflict !== undefined) {
              props.onCreatedAndBound(afterConflict);
              return;
            }
            throw failure instanceof Error
              ? failure
              : new Error(
                  'Не удалось создать тип документа с предложенным кодом. Обновите справочник и выберите тип вручную.',
                );
          }
        }
        const created = await props.api.createDocumentType({
          code: generateInternalCode('DocumentType'),
          display_name:
            props.proposal.display_label.trim().length > 0
              ? props.proposal.display_label.trim()
              : 'Документ без кода',
          default_metadata_schema: emptyMetadataSchema(),
        });
        props.onCreatedAndBound(created);
      } catch (failure: unknown) {
        props.onError(failure);
      } finally {
        props.setBusy(false);
      }
    })();
  };

  return (
    <fieldset className="info-card">
      <legend>{props.proposal.display_label || 'Документ без названия'}</legend>
      <p>
        Назначение: {authoringSubjectBindingLabel(props.proposal.subject_binding)} · Режим:{' '}
        {captureLabel}
      </p>
      <p role="status" className="banner banner-conflict">
        Тип документа ещё не связан со справочником.
      </p>
      {props.proposal.metadata_fields_proposed.length > 0 ? (
        <p className="meta">
          Предлагаемые реквизиты:{' '}
          {props.proposal.metadata_fields_proposed
            .map((meta) => meta.label || meta.field_code)
            .join(', ')}
        </p>
      ) : null}
      <details>
        <summary>Источник в файле импорта</summary>
        <p className="meta">
          Строка {props.proposal.source_row}
          {knownCode !== null
            ? ` · предложенный код ${knownCode}`
            : ' · устойчивый код не определён'}
        </p>
      </details>
      <label>
        Выбрать существующий тип
        <select
          aria-label={`Существующий тип для ${props.proposal.display_label}`}
          value={selectedTypeId}
          disabled={props.disabled || props.documentTypes === null}
          onChange={(event) => setSelectedTypeId(event.target.value)}
        >
          <option value="">Выберите тип документа</option>
          {(props.documentTypes ?? []).map((row) => (
            <option key={row.id} value={row.id}>
              {documentTypeBusinessLabel(row)}
            </option>
          ))}
        </select>
      </label>
      <div className="actions">
        <button
          type="button"
          disabled={props.disabled || selectedTypeId.trim().length === 0}
          onClick={() => props.onBindExisting(selectedTypeId)}
        >
          Выбрать существующий тип
        </button>
        <button type="button" disabled={props.disabled} onClick={createAndBind}>
          Создать тип документа и привязать
        </button>
        <button type="button" disabled={props.disabled} onClick={() => setConfirmExclude(true)}>
          Исключить из чек-листа
        </button>
      </div>
      {confirmExclude ? (
        <div role="dialog" aria-label="Подтверждение исключения документа" className="banner">
          <p>
            Исключить «{props.proposal.display_label}» из этого черновика? Тип документа не будет
            создан, исходный файл импорта не изменится.
          </p>
          <div className="actions">
            <button
              type="button"
              onClick={() => {
                setConfirmExclude(false);
                props.onExclude();
              }}
            >
              Подтвердить исключение
            </button>
            <button type="button" onClick={() => setConfirmExclude(false)}>
              Отмена
            </button>
          </div>
        </div>
      ) : null}
    </fieldset>
  );
}

function DocumentEditor(props: {
  readonly index: number;
  readonly document: AuthoringDocumentRequirementForm;
  readonly documentTypes: DocumentTypeAdmin[] | null;
  readonly disabled: boolean;
  readonly onChange: (document: AuthoringDocumentRequirementForm) => void;
  readonly onRemove: () => void;
  readonly onMoveUp: () => void;
  readonly onMoveDown: () => void;
}): ReactElement {
  const document = props.document;
  return (
    <fieldset>
      <legend>Документ {props.index + 1}</legend>
      <label htmlFor={`tad_doc_type_${props.index}`}>
        Тип документа
        <select
          id={`tad_doc_type_${props.index}`}
          value={document.document_type_id}
          disabled={props.disabled || props.documentTypes === null}
          onChange={(event) => {
            const nextTypeId = event.target.value;
            const selected = (props.documentTypes ?? []).find((row) => row.id === nextTypeId);
            props.onChange({
              ...document,
              document_type_id: nextTypeId,
              metadata_fields: metadataFieldsFromDocumentTypeDefaultSchema(
                selected?.default_metadata_schema,
              ),
            });
          }}
        >
          <option value="">Выберите тип документа</option>
          {(props.documentTypes ?? []).map((row) => (
            <option key={row.id} value={row.id}>
              {documentTypeBusinessLabel(row)}
            </option>
          ))}
        </select>
      </label>
      <label htmlFor={`tad_doc_capture_${props.index}`}>
        Режим захвата
        <select
          id={`tad_doc_capture_${props.index}`}
          value={document.capture_mode}
          disabled={props.disabled}
          onChange={(event) =>
            props.onChange({
              ...document,
              capture_mode: event.target.value as AuthoringDocumentRequirementForm['capture_mode'],
            })
          }
        >
          {AUTHORING_CAPTURE_MODES.map((mode) => (
            <option key={mode} value={mode}>
              {authoringCaptureModeLabel(mode)}
            </option>
          ))}
        </select>
      </label>
      <label htmlFor={`tad_doc_label_${props.index}`}>
        Отображаемая подпись
        <input
          id={`tad_doc_label_${props.index}`}
          value={document.display_label}
          disabled={props.disabled}
          onChange={(event) => props.onChange({ ...document, display_label: event.target.value })}
        />
      </label>
      <label htmlFor={`tad_doc_subject_${props.index}`}>
        Subject binding
        <input
          id={`tad_doc_subject_${props.index}`}
          value={document.subject_binding}
          disabled={props.disabled}
          onChange={(event) => props.onChange({ ...document, subject_binding: event.target.value })}
        />
      </label>
      <RequiredRuleEditor
        id={`tad_doc_required_${props.index}`}
        value={document.required_rule}
        disabled={props.disabled}
        onChange={(required_rule) => props.onChange({ ...document, required_rule })}
      />
      <ApplicabilityRuleEditor
        id={`tad_doc_applicability_${props.index}`}
        value={document.applicability_rule}
        disabled={props.disabled}
        onChange={(applicability_rule) => props.onChange({ ...document, applicability_rule })}
      />
      <label htmlFor={`tad_doc_verification_${props.index}`}>
        Примечание верификации
        <input
          id={`tad_doc_verification_${props.index}`}
          value={document.verification_note}
          disabled={props.disabled}
          onChange={(event) =>
            props.onChange({ ...document, verification_note: event.target.value })
          }
        />
      </label>
      <h4>Метаданные документа</h4>
      <button
        type="button"
        disabled={props.disabled}
        onClick={() =>
          props.onChange({
            ...document,
            metadata_fields: reindexOrdinals([
              ...document.metadata_fields,
              emptyDocumentMetadataField(document.metadata_fields.length),
            ]),
          })
        }
      >
        Добавить поле метаданных
      </button>
      {document.metadata_fields.map((meta, metaIndex) => (
        <fieldset key={`meta-${metaIndex}`}>
          <legend>Метаданные {metaIndex + 1}</legend>
          <label htmlFor={`tad_doc_meta_code_${props.index}_${metaIndex}`}>
            Код
            <input
              id={`tad_doc_meta_code_${props.index}_${metaIndex}`}
              value={meta.field_code}
              disabled={props.disabled}
              onChange={(event) =>
                props.onChange({
                  ...document,
                  metadata_fields: document.metadata_fields.map((row, rowIndex) =>
                    rowIndex === metaIndex ? { ...row, field_code: event.target.value } : row,
                  ),
                })
              }
            />
          </label>
          <label htmlFor={`tad_doc_meta_label_${props.index}_${metaIndex}`}>
            Подпись
            <input
              id={`tad_doc_meta_label_${props.index}_${metaIndex}`}
              value={meta.label}
              disabled={props.disabled}
              onChange={(event) =>
                props.onChange({
                  ...document,
                  metadata_fields: document.metadata_fields.map((row, rowIndex) =>
                    rowIndex === metaIndex ? { ...row, label: event.target.value } : row,
                  ),
                })
              }
            />
          </label>
          <label htmlFor={`tad_doc_meta_type_${props.index}_${metaIndex}`}>
            Тип
            <select
              id={`tad_doc_meta_type_${props.index}_${metaIndex}`}
              value={meta.value_type}
              disabled={props.disabled}
              onChange={(event) => {
                const nextType = event.target.value as AuthoringFieldForm['value_type'];
                const result = validationAfterValueTypeChange(meta.validation, nextType);
                if (result.blocked) {
                  window.alert(
                    'Сначала преобразуйте историческое правило проверки в Validation v1, затем меняйте тип значения.',
                  );
                  return;
                }
                props.onChange({
                  ...document,
                  metadata_fields: document.metadata_fields.map((row, rowIndex) =>
                    rowIndex === metaIndex
                      ? {
                          ...row,
                          value_type: nextType,
                          validation: result.validation,
                        }
                      : row,
                  ),
                });
              }}
            >
              {AUTHORING_FIELD_VALUE_TYPES.map((type) => (
                <option key={type} value={type}>
                  {authoringValueTypeLabel(type)}
                </option>
              ))}
            </select>
          </label>
          <label htmlFor={`tad_doc_meta_required_${props.index}_${metaIndex}`}>
            <input
              id={`tad_doc_meta_required_${props.index}_${metaIndex}`}
              type="checkbox"
              checked={meta.required}
              disabled={props.disabled}
              onChange={(event) =>
                props.onChange({
                  ...document,
                  metadata_fields: document.metadata_fields.map((row, rowIndex) =>
                    rowIndex === metaIndex ? { ...row, required: event.target.checked } : row,
                  ),
                })
              }
            />{' '}
            Обязательное
          </label>
          <div>
            <p className="meta">Правила проверки (Validation v1)</p>
            <CfgValValidationEditor
              valueType={meta.value_type}
              validation={meta.validation}
              disabled={props.disabled}
              ariaPrefix={`метаданные документа ${props.index + 1} поле ${metaIndex + 1}`}
              onChange={(validation) =>
                props.onChange({
                  ...document,
                  metadata_fields: document.metadata_fields.map((row, rowIndex) =>
                    rowIndex === metaIndex ? { ...row, validation } : row,
                  ),
                })
              }
            />
          </div>
          <button
            type="button"
            disabled={props.disabled}
            onClick={() =>
              props.onChange({
                ...document,
                metadata_fields: reindexOrdinals(
                  document.metadata_fields.filter((_, rowIndex) => rowIndex !== metaIndex),
                ),
              })
            }
          >
            Удалить поле метаданных
          </button>
        </fieldset>
      ))}
      <div className="actions">
        <button type="button" disabled={props.disabled} onClick={props.onMoveUp}>
          Выше
        </button>
        <button type="button" disabled={props.disabled} onClick={props.onMoveDown}>
          Ниже
        </button>
        <button type="button" disabled={props.disabled} onClick={props.onRemove}>
          Удалить документ
        </button>
      </div>
    </fieldset>
  );
}

function SectionsArea(props: {
  readonly form: AuthoringPayloadForm;
  readonly disabled: boolean;
  readonly onChange: (updater: (current: AuthoringPayloadForm) => AuthoringPayloadForm) => void;
}): ReactElement {
  const [expandedSectionIndex, setExpandedSectionIndex] = useState<number | null>(null);
  const [expandedItemKey, setExpandedItemKey] = useState<string | null>(null);
  return (
    <>
      <div className="actions">
        <button
          type="button"
          disabled={props.disabled}
          onClick={() => {
            const nextIndex = props.form.sections.length;
            props.onChange((current) => ({
              ...current,
              sections: reindexOrdinals([
                ...current.sections,
                emptySection(current.sections.length),
              ]),
            }));
            setExpandedSectionIndex(nextIndex);
            setExpandedItemKey(null);
          }}
        >
          Добавить раздел
        </button>
      </div>
      {props.form.sections.length === 0 ? (
        <p role="status" className="banner">
          Разделы ещё не добавлены.
        </p>
      ) : null}
      <ul className="authoring-field-list" aria-label="Разделы черновика">
        {props.form.sections.map((section, index) => {
          const items = props.form.items.filter(
            (item) => item.section_code === section.section_code,
          );
          const expanded = expandedSectionIndex === index;
          const title =
            section.title.trim().length > 0 ? section.title.trim() : `Раздел ${index + 1}`;
          return (
            <li
              key={`section-${index}`}
              className={
                expanded ? 'authoring-field-row authoring-field-row-expanded' : 'authoring-field-row'
              }
            >
              <div className="authoring-field-row-main">
                <button
                  type="button"
                  className="authoring-field-summary"
                  aria-expanded={expanded}
                  title={title}
                  onClick={() => {
                    setExpandedSectionIndex((current) => (current === index ? null : index));
                    setExpandedItemKey(null);
                  }}
                >
                  <span className="authoring-field-title">
                    {index + 1}. {title}
                  </span>
                  <p className="authoring-field-summary-line meta">
                    пунктов: {items.length}
                    {section.section_code.trim().length > 0
                      ? ` · код ${section.section_code}`
                      : ''}
                  </p>
                </button>
                <div className="authoring-field-actions">
                  <button
                    type="button"
                    className="authoring-field-icon-btn"
                    title="Выше"
                    aria-label="Выше"
                    disabled={props.disabled || index === 0}
                    onClick={() =>
                      props.onChange((current) => ({
                        ...current,
                        sections: reindexOrdinals(moveItem(current.sections, index, index - 1)),
                      }))
                    }
                  >
                    <span aria-hidden="true">↑</span>
                  </button>
                  <button
                    type="button"
                    className="authoring-field-icon-btn"
                    title="Ниже"
                    aria-label="Ниже"
                    disabled={props.disabled || index >= props.form.sections.length - 1}
                    onClick={() =>
                      props.onChange((current) => ({
                        ...current,
                        sections: reindexOrdinals(moveItem(current.sections, index, index + 1)),
                      }))
                    }
                  >
                    <span aria-hidden="true">↓</span>
                  </button>
                  <button
                    type="button"
                    className="authoring-field-icon-btn"
                    title="Удалить раздел"
                    aria-label="Удалить раздел"
                    disabled={props.disabled}
                    onClick={() => {
                      if (
                        !window.confirm(
                          `Удалить раздел «${title}» и его пункты (${items.length})?`,
                        )
                      ) {
                        return;
                      }
                      props.onChange((current) => ({
                        ...current,
                        sections: reindexOrdinals(
                          current.sections.filter((_, rowIndex) => rowIndex !== index),
                        ),
                        items: current.items.filter(
                          (item) => item.section_code !== section.section_code,
                        ),
                      }));
                      setExpandedSectionIndex(null);
                      setExpandedItemKey(null);
                    }}
                  >
                    <span aria-hidden="true">🗑</span>
                  </button>
                </div>
              </div>
              {expanded ? (
                <div className="authoring-field-panel">
                  <SectionEditor
                    index={index}
                    section={section}
                    items={items}
                    disabled={props.disabled}
                    expandedItemKey={expandedItemKey}
                    onExpandedItemKeyChange={setExpandedItemKey}
                    onChange={(next) =>
                      props.onChange((current) => {
                        const previousCode = current.sections[index]?.section_code;
                        const sections = current.sections.map((row, rowIndex) =>
                          rowIndex === index ? next : row,
                        );
                        const nextItems =
                          previousCode === undefined || previousCode === next.section_code
                            ? current.items
                            : current.items.map((item) =>
                                item.section_code === previousCode
                                  ? { ...item, section_code: next.section_code }
                                  : item,
                              );
                        return { ...current, sections: reindexOrdinals(sections), items: nextItems };
                      })
                    }
                    onItemsChange={(nextItems) =>
                      props.onChange((current) => ({
                        ...current,
                        items: [
                          ...current.items.filter(
                            (item) => item.section_code !== section.section_code,
                          ),
                          ...nextItems,
                        ],
                      }))
                    }
                  />
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </>
  );
}

function SectionEditor(props: {
  readonly index: number;
  readonly section: AuthoringSectionForm;
  readonly items: AuthoringItemForm[];
  readonly disabled: boolean;
  readonly expandedItemKey: string | null;
  readonly onExpandedItemKeyChange: (key: string | null) => void;
  readonly onChange: (section: AuthoringSectionForm) => void;
  readonly onItemsChange: (items: AuthoringItemForm[]) => void;
}): ReactElement {
  const section = props.section;
  return (
    <>
      <label htmlFor={`tad_section_code_${props.index}`}>
        Код раздела
        <input
          id={`tad_section_code_${props.index}`}
          value={section.section_code}
          disabled={props.disabled}
          onChange={(event) => props.onChange({ ...section, section_code: event.target.value })}
        />
      </label>
      <label htmlFor={`tad_section_title_${props.index}`}>
        Название
        <input
          id={`tad_section_title_${props.index}`}
          value={section.title}
          disabled={props.disabled}
          onChange={(event) => props.onChange({ ...section, title: event.target.value })}
        />
      </label>
      <div className="actions">
        <button
          type="button"
          disabled={props.disabled}
          onClick={() => {
            const nextItem = emptyItem(section.section_code, props.items.length);
            const nextKey = `s${props.index}:i${props.items.length}`;
            props.onItemsChange(reindexOrdinals([...props.items, nextItem]));
            props.onExpandedItemKeyChange(nextKey);
          }}
        >
          Добавить пункт
        </button>
      </div>
      <p className="meta">
        Оценки платформы фиксированы: PASS / FAIL / NOT_APPLICABLE — переопределение недоступно.
      </p>
      <ul className="authoring-field-list" aria-label={`Пункты раздела ${props.index + 1}`}>
        {props.items.map((item, itemIndex) => {
          const itemKey = `s${props.index}:i${itemIndex}`;
          const expanded = props.expandedItemKey === itemKey;
          const label =
            item.label.trim().length > 0 ? item.label.trim() : `Пункт ${itemIndex + 1}`;
          return (
            <li
              key={itemKey}
              className={
                expanded ? 'authoring-field-row authoring-field-row-expanded' : 'authoring-field-row'
              }
            >
              <div className="authoring-field-row-main">
                <button
                  type="button"
                  className="authoring-field-summary"
                  aria-expanded={expanded}
                  title={label}
                  onClick={() =>
                    props.onExpandedItemKeyChange(expanded ? null : itemKey)
                  }
                >
                  <span className="authoring-field-title">
                    {item.item_code.trim().length > 0 ? `${item.item_code} · ` : ''}
                    {label}
                  </span>
                </button>
                <div className="authoring-field-actions">
                  <button
                    type="button"
                    className="authoring-field-icon-btn"
                    title="Выше"
                    aria-label="Выше"
                    disabled={props.disabled || itemIndex === 0}
                    onClick={() =>
                      props.onItemsChange(
                        reindexOrdinals(moveItem(props.items, itemIndex, itemIndex - 1)),
                      )
                    }
                  >
                    <span aria-hidden="true">↑</span>
                  </button>
                  <button
                    type="button"
                    className="authoring-field-icon-btn"
                    title="Ниже"
                    aria-label="Ниже"
                    disabled={props.disabled || itemIndex >= props.items.length - 1}
                    onClick={() =>
                      props.onItemsChange(
                        reindexOrdinals(moveItem(props.items, itemIndex, itemIndex + 1)),
                      )
                    }
                  >
                    <span aria-hidden="true">↓</span>
                  </button>
                  <button
                    type="button"
                    className="authoring-field-icon-btn"
                    title="Удалить пункт"
                    aria-label="Удалить пункт"
                    disabled={props.disabled}
                    onClick={() => {
                      if (!window.confirm(`Удалить пункт «${label}»?`)) {
                        return;
                      }
                      props.onItemsChange(
                        reindexOrdinals(
                          props.items.filter((_, rowIndex) => rowIndex !== itemIndex),
                        ),
                      );
                      props.onExpandedItemKeyChange(null);
                    }}
                  >
                    <span aria-hidden="true">🗑</span>
                  </button>
                </div>
              </div>
              {expanded ? (
                <div className="authoring-field-panel">
                  <ItemEditor
                    index={itemIndex}
                    item={item}
                    disabled={props.disabled}
                    onChange={(next) =>
                      props.onItemsChange(
                        props.items.map((row, rowIndex) =>
                          rowIndex === itemIndex ? next : row,
                        ),
                      )
                    }
                  />
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </>
  );
}

function ItemEditor(props: {
  readonly index: number;
  readonly item: AuthoringItemForm;
  readonly disabled: boolean;
  readonly onChange: (item: AuthoringItemForm) => void;
}): ReactElement {
  const item = props.item;
  return (
    <>
      <label htmlFor={`tad_item_code_${props.index}`}>
        Код пункта
        <input
          id={`tad_item_code_${props.index}`}
          value={item.item_code}
          disabled={props.disabled}
          onChange={(event) => props.onChange({ ...item, item_code: event.target.value })}
        />
      </label>
      <label htmlFor={`tad_item_label_${props.index}`}>
        Подпись
        <input
          id={`tad_item_label_${props.index}`}
          value={item.label}
          disabled={props.disabled}
          onChange={(event) => props.onChange({ ...item, label: event.target.value })}
        />
      </label>
      <label htmlFor={`tad_item_owner_${props.index}`}>
        Владелец
        <select
          id={`tad_item_owner_${props.index}`}
          value={item.owner_code}
          disabled={props.disabled}
          onChange={(event) =>
            props.onChange({
              ...item,
              owner_code: event.target.value as AuthoringItemForm['owner_code'],
            })
          }
        >
          {AUTHORING_OWNER_CODES.map((owner) => (
            <option key={owner} value={owner}>
              {authoringOwnerLabel(owner)}
            </option>
          ))}
        </select>
      </label>
      <RequiredRuleEditor
        id={`tad_item_required_${props.index}`}
        value={item.required_rule}
        disabled={props.disabled}
        onChange={(required_rule) => props.onChange({ ...item, required_rule })}
      />
      <ApplicabilityRuleEditor
        id={`tad_item_applicability_${props.index}`}
        value={item.applicability_rule}
        disabled={props.disabled}
        onChange={(applicability_rule) => props.onChange({ ...item, applicability_rule })}
      />
      <label htmlFor={`tad_item_norm_${props.index}`}>
        Нормативная ссылка
        <input
          id={`tad_item_norm_${props.index}`}
          value={item.normative_reference}
          disabled={props.disabled}
          onChange={(event) => props.onChange({ ...item, normative_reference: event.target.value })}
        />
      </label>
      <label htmlFor={`tad_item_pdf_${props.index}`}>
        PDF-подпись
        <input
          id={`tad_item_pdf_${props.index}`}
          value={item.pdf_label}
          disabled={props.disabled}
          onChange={(event) => props.onChange({ ...item, pdf_label: event.target.value })}
        />
      </label>
      <details className="technical">
        <summary>Валидация пункта</summary>
        <button
          type="button"
          disabled={props.disabled}
          onClick={() =>
            props.onChange({
              ...item,
              validation: {
                ...item.validation,
                fields: [
                  ...item.validation.fields,
                  {
                    field_key: `val_${item.validation.fields.length + 1}`,
                    value_type: 'TEXT',
                    required_when: 'ALWAYS',
                  },
                ],
              },
            })
          }
        >
          Добавить поле валидации
        </button>
        {item.validation.fields.map((val, valIndex) => (
          <fieldset key={`val-${valIndex}`}>
            <legend>Поле валидации {valIndex + 1}</legend>
            <p className="meta">Ключ: {val.field_key}</p>
            <label htmlFor={`tad_item_val_type_${props.index}_${valIndex}`}>
              Тип
              <select
                id={`tad_item_val_type_${props.index}_${valIndex}`}
                value={val.value_type}
                disabled={props.disabled}
                onChange={(event) =>
                  props.onChange({
                    ...item,
                    validation: {
                      ...item.validation,
                      fields: item.validation.fields.map((row, rowIndex) =>
                        rowIndex === valIndex
                          ? {
                              ...row,
                              value_type: event.target
                                .value as (typeof ITEM_VALIDATION_VALUE_TYPES)[number],
                            }
                          : row,
                      ),
                    },
                  })
                }
              >
                {ITEM_VALIDATION_VALUE_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </label>
            <label htmlFor={`tad_item_val_when_${props.index}_${valIndex}`}>
              Обязательность
              <select
                id={`tad_item_val_when_${props.index}_${valIndex}`}
                value={val.required_when}
                disabled={props.disabled}
                onChange={(event) =>
                  props.onChange({
                    ...item,
                    validation: {
                      ...item.validation,
                      fields: item.validation.fields.map((row, rowIndex) =>
                        rowIndex === valIndex
                          ? {
                              ...row,
                              required_when: event.target
                                .value as (typeof ITEM_VALIDATION_REQUIRED_WHEN)[number],
                            }
                          : row,
                      ),
                    },
                  })
                }
              >
                {ITEM_VALIDATION_REQUIRED_WHEN.map((when) => (
                  <option key={when} value={when}>
                    {when}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              disabled={props.disabled}
              onClick={() =>
                props.onChange({
                  ...item,
                  validation: {
                    ...item.validation,
                    fields: item.validation.fields.filter((_, rowIndex) => rowIndex !== valIndex),
                  },
                })
              }
            >
              Удалить поле валидации
            </button>
          </fieldset>
        ))}
      </details>
    </>
  );
}

function PresentationArea(props: {
  readonly form: AuthoringPayloadForm;
  readonly disabled: boolean;
  readonly onChange: (updater: (current: AuthoringPayloadForm) => AuthoringPayloadForm) => void;
}): ReactElement {
  const presentation = props.form.presentation;
  return (
    <fieldset>
      <legend>Отображение / PDF</legend>
      <p className="meta">
        Настройки представления для экранного и PDF-отображения. Публикация и активация версии
        выполняются отдельно.
      </p>
      <label htmlFor="tad_presentation_enabled">
        <input
          id="tad_presentation_enabled"
          type="checkbox"
          checked={presentation.enabled}
          disabled={props.disabled}
          onChange={(event) =>
            props.onChange((current) => ({
              ...current,
              presentation: { ...current.presentation, enabled: event.target.checked },
            }))
          }
        />{' '}
        Включить presentation schema v1
      </label>
      <label htmlFor="tad_layout_title">
        Заголовок layout
        <input
          id="tad_layout_title"
          value={presentation.layout_title}
          disabled={props.disabled || !presentation.enabled}
          onChange={(event) =>
            props.onChange((current) => ({
              ...current,
              presentation: { ...current.presentation, layout_title: event.target.value },
            }))
          }
        />
      </label>
      <label htmlFor="tad_pdf_title">
        Заголовок PDF
        <input
          id="tad_pdf_title"
          value={presentation.pdf_title}
          disabled={props.disabled || !presentation.enabled}
          onChange={(event) =>
            props.onChange((current) => ({
              ...current,
              presentation: { ...current.presentation, pdf_title: event.target.value },
            }))
          }
        />
      </label>
      <label htmlFor="tad_pdf_normative">
        <input
          id="tad_pdf_normative"
          type="checkbox"
          checked={presentation.pdf_include_normative_references}
          disabled={props.disabled || !presentation.enabled}
          onChange={(event) =>
            props.onChange((current) => ({
              ...current,
              presentation: {
                ...current.presentation,
                pdf_include_normative_references: event.target.checked,
              },
            }))
          }
        />{' '}
        Включать нормативные ссылки в PDF
      </label>
      <details className="technical">
        <summary>Технические параметры представления</summary>
        <p className="meta">
          Контролы соответствуют presentation.schema_version=1 и структурированным полям layout/pdf.
          Произвольный executable markup недоступен.
        </p>
      </details>
    </fieldset>
  );
}

function ValidationPreviewArea(props: {
  readonly dirty: boolean;
  readonly draftId: string;
  readonly draftStatus: TemplateAuthoringDraft['status'];
  readonly draftVersion: string;
  readonly validation: TemplateAuthoringValidationReport | null;
  readonly form: AuthoringPayloadForm | null;
  readonly preview: TemplateAuthoringPreview | null;
  readonly previewView: TemplateAuthoringPreviewView;
  readonly busy: boolean;
  readonly onPreviewViewChange: (view: TemplateAuthoringPreviewView) => void;
  readonly onValidate: () => void;
  readonly onPreview: () => void;
  readonly onJump: (issue: TemplateAuthoringValidationIssue) => void;
}): ReactElement {
  const [expandedHeaderIssueKey, setExpandedHeaderIssueKey] = useState<string | null>(null);
  const [expandedTechKey, setExpandedTechKey] = useState<string | null>(null);
  const errorCount = props.validation?.errors.length ?? 0;
  const warningCount = props.validation?.warnings.length ?? 0;
  const headerErrors =
    props.validation?.errors.filter((issue) => isUnresolvedHeaderFieldCandidateIssue(issue)) ?? [];
  const otherIssues = props.validation
    ? [
        ...props.validation.errors.filter((issue) => !isUnresolvedHeaderFieldCandidateIssue(issue)),
        ...props.validation.warnings,
      ]
    : [];
  const review = props.form?.header_binding_review ?? null;

  return (
    <>
      {props.dirty ? (
        <p role="status" className="banner banner-conflict">
          Есть несохранённые изменения. Серверная проверка и превью используют последнее сохранённое
          состояние.
        </p>
      ) : null}
      <div className="actions">
        <button type="button" disabled={props.busy} onClick={props.onValidate}>
          Серверная проверка
        </button>
      </div>
      {props.validation === null ? (
        <p role="status">Отчёт серверной проверки ещё не получен.</p>
      ) : (
        <>
          <p>
            Статус после проверки: {authoringDraftStatusLabel(props.validation.status)} · ошибок{' '}
            {errorCount} · предупреждений {warningCount}
          </p>
          {errorCount > 0 ? (
            <p role="alert" className="banner banner-closed">
              Черновик не готов к публикации: есть ошибки серверной проверки.
            </p>
          ) : (
            <p role="status" className="banner">
              Ошибок нет. Неизменяемую версию можно сохранить через «Применить изменения»; сделать
              активной — отдельным действием.
            </p>
          )}
          {headerErrors.length > 0 ? (
            <section
              className="validation-header-issues"
              aria-label="Ошибки полей шапки"
              role="alert"
            >
              <p className="validation-header-issues-summary">
                {headerErrors.length}{' '}
                {headerErrors.length === 1
                  ? 'ошибка'
                  : headerErrors.length < 5
                    ? 'ошибки'
                    : 'ошибок'}
                : необходимо принять решения по найденным полям шапки. До этого публикация
                недоступна.
              </p>
              <ul className="validation-header-issue-list">
                {headerErrors.map((issue, index) => {
                  const key = `${issue.code}-${issue.subject_id ?? index}`;
                  const display = unresolvedHeaderCandidateDisplay(
                    issue,
                    review?.candidates,
                    review?.source_sheet ?? null,
                  );
                  const detailsOpen = expandedHeaderIssueKey === key;
                  const techOpen = expandedTechKey === key;
                  const jumpLabel = `Перейти к полю «${display.label}»`;
                  return (
                    <li key={key} className="validation-header-issue-row">
                      <div className="validation-header-issue-main">
                        <span className="validation-header-issue-title" title={display.label}>
                          {display.label}
                        </span>
                        <span className="validation-header-issue-status">Требует решения</span>
                        <div className="validation-header-issue-actions">
                          <button
                            type="button"
                            className="authoring-field-icon-btn"
                            title="Подробности"
                            aria-label={`Подробности по полю «${display.label}»`}
                            aria-expanded={detailsOpen}
                            onClick={() =>
                              setExpandedHeaderIssueKey((current) =>
                                current === key ? null : key,
                              )
                            }
                          >
                            ⓘ
                          </button>
                          <button
                            type="button"
                            className="authoring-field-icon-btn"
                            title={jumpLabel}
                            aria-label={jumpLabel}
                            onClick={() => props.onJump(issue)}
                          >
                            →
                          </button>
                        </div>
                      </div>
                      {detailsOpen ? (
                        <div className="validation-header-issue-details">
                          <p>Поле: «{display.label}»</p>
                          {display.sourceSheet !== null && display.sourceRow !== null ? (
                            <p>
                              Источник: лист Excel «{display.sourceSheet}», строка{' '}
                              {display.sourceRow}
                            </p>
                          ) : null}
                          <p>
                            Что сделать: в блоке «Найденные поля шапки» выберите действие для этого
                            кандидата — принять предложенную привязку, выбрать другое поле, создать
                            новое поле или исключить ошибочно распознанный элемент.
                          </p>
                          <details
                            className="technical"
                            open={techOpen}
                            onToggle={(event) => {
                              const open = (event.currentTarget as HTMLDetailsElement).open;
                              setExpandedTechKey(open ? key : null);
                            }}
                          >
                            <summary>Технические сведения</summary>
                            <p className="meta">
                              {issue.code} · {issue.subject_kind}
                              {issue.subject_id !== null ? ` · ${issue.subject_id}` : ''}
                            </p>
                            <p className="meta">{issue.message}</p>
                          </details>
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </section>
          ) : null}
          {otherIssues.map((issue, index) => (
            <div
              key={`${issue.code}-${index}`}
              role={issue.severity === 'ERROR' ? 'alert' : 'status'}
              className={issue.severity === 'ERROR' ? 'banner banner-closed' : 'banner'}
            >
              <p>
                {issue.severity === 'ERROR' ? 'Ошибка' : 'Предупреждение'}: {issue.message}
              </p>
              <p className="meta">
                {issue.code} · {validationIssueArea(issue)}
                {issue.subject_id !== null ? ` · ${issue.subject_id}` : ''}
              </p>
              <button type="button" onClick={() => props.onJump(issue)}>
                К области «{validationIssueArea(issue)}»
              </button>
            </div>
          ))}
        </>
      )}
      <h3>Превью черновика</h3>
      <p className="meta" data-testid="authoring-draft-preview-context">
        Источник: редактируемый черновик · статус {authoringDraftStatusLabel(props.draftStatus)} ·
        ревизия {props.draftVersion}. Это не просмотр архивной/активной опубликованной версии
        (тот открывается кнопкой «Посмотреть» в списке версий чек-листа).
      </p>
      <label htmlFor="tad_preview_view">
        Режим превью
        <select
          id="tad_preview_view"
          value={props.previewView}
          disabled={props.busy}
          onChange={(event) =>
            props.onPreviewViewChange(event.target.value as TemplateAuthoringPreviewView)
          }
        >
          {AUTHORING_PREVIEW_VIEWS.map((view) => (
            <option key={view} value={view}>
              {authoringPreviewViewLabel(view)}
            </option>
          ))}
        </select>
      </label>
      <button type="button" disabled={props.busy} onClick={props.onPreview}>
        Загрузить превью сохранённого черновика
      </button>
      {props.preview !== null ? (
        <>
          <p className="meta" data-testid="authoring-draft-preview-meta">
            Превью сохранённого черновика · {authoringPreviewViewLabel(props.preview.view)} ·
            draft_id {props.preview.draft_id} · draft_version {props.preview.draft_version}
          </p>
          {(() => {
            const schemaFields = Array.isArray(props.preview.resolved_schema.fields)
              ? (props.preview.resolved_schema.fields as readonly Record<string, unknown>[])
              : [];
            if (schemaFields.length < 1) {
              return (
                <p className="meta" role="status">
                  Поля предзаполнения в этом снимке отсутствуют.
                </p>
              );
            }
            return (
              <ol
                className="authoring-preview-field-order"
                aria-label="Порядок полей сохранённого черновика"
              >
                {schemaFields.map((row, index) => {
                  const label =
                    typeof row.label === 'string' && row.label.trim().length > 0
                      ? row.label.trim()
                      : typeof row.field_key === 'string'
                        ? row.field_key
                        : `Поле ${index + 1}`;
                  return (
                    <li key={`${String(row.field_key ?? index)}:${index}`}>
                      {index + 1}. {label}
                      {typeof row.value_type === 'string' ? ` · ${row.value_type}` : ''}
                    </li>
                  );
                })}
              </ol>
            );
          })()}
          <pre aria-label="Серверное превью черновика">
            {JSON.stringify(props.preview.resolved_schema, null, 2)}
          </pre>
        </>
      ) : null}
    </>
  );
}

function PublishArea(props: {
  readonly draft: TemplateAuthoringDraft;
  readonly validation: TemplateAuthoringValidationReport | null;
  readonly dirty: boolean;
  readonly versionCode: string;
  readonly published: PublishedTemplateVersionResult | null;
}): ReactElement {
  return (
    <>
      <p className="meta">
        Сохраните черновик и вернитесь к чек-листу. «Применить изменения» / публикация сохраняет
        неизменяемую версию. «Сделать активной» — отдельное подтверждаемое действие и не выполняется
        автоматически при публикации.
      </p>
      {props.dirty ? (
        <p role="status" className="banner banner-conflict">
          Есть несохранённые изменения. Сохраните черновик перед выходом.
        </p>
      ) : null}
      {props.validation !== null && props.validation.errors.length > 0 ? (
        <p role="alert" className="banner banner-closed">
          Есть ошибки проверки. Исправьте их перед применением изменений.
        </p>
      ) : null}
      <TechnicalDetails>
        <p>Предлагаемый код версии: {props.versionCode.trim() || '—'}</p>
        {props.draft.published_template_version_id !== null &&
        props.draft.published_template_version_id !== undefined ? (
          <p>Связанная template_version_id: {props.draft.published_template_version_id}</p>
        ) : null}
        {props.published !== null ? (
          <p>
            Последняя техническая публикация: {props.published.version_code} (
            {props.published.id})
          </p>
        ) : null}
      </TechnicalDetails>
    </>
  );
}
